import { GoogleWorkspaceService, googleFileKind, googlePreviewUrl, googleGmailUrl } from "./google-workspace-service.js";
import { isOfficeSelfPreviewMime, renderOfficePreview } from "./office-preview.js";
import { esc } from "./html-escape.js";

const SUPPORTED_ENTITIES = new Set(["program", "project", "phase", "activity", "gate", "deliverable", "agreement", "risk", "organization"]);
const GOOGLE_NATIVE_MIME = new Set([
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation"
]);
const NAYSOR_RELAY_MIME = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/rtf",
  "text/rtf",
  "text/plain",
  "text/csv"
]);

const googleLanguage = () => ["ko", "en", "id"].includes(localStorage.getItem("naysor-project-control-language")) ? localStorage.getItem("naysor-project-control-language") : "ko";
const googleLocale = () => googleLanguage() === "en" ? "en-GB" : googleLanguage() === "id" ? "id-ID" : "ko-KR";
const GOOGLE_INTERFACE = {
  "연결 상태 확인 중": ["Checking connection", "Memeriksa koneksi"],
  "내 Drive": ["My Drive", "Drive Saya"],
  "내 Calendar · Meet": ["My Calendar · Meet", "Calendar · Meet Saya"],
  "내 Gmail": ["My Gmail", "Gmail Saya"],
  "내 Meet 기록": ["My Meet History", "Riwayat Meet Saya"],
  "내 연결": ["My Connection", "Koneksi Saya"],
  "연결 대상": ["Link target", "Target tautan"],
  "연결 대상 미선택": ["No target selected", "Target belum dipilih"],
  "연결 자료": ["Linked context", "Konteks tertaut"],
  "현재 연결 문서": ["Linked documents", "Dokumen tertaut"],
  "이 항목에 연결된 문서·메일·일정이 없음": ["No linked documents, mail or events", "Tidak ada dokumen, email, atau acara tertaut"],
  "이 항목에 연결된 Google 자료가 없음": ["No Google context linked to this item", "Tidak ada konteks Google yang tertaut"],
  "내 받은 메일 찾아 연결": ["Find and link received mail", "Cari dan tautkan email masuk"],
  "내 Drive 탐색": ["Browse My Drive", "Jelajahi Drive Saya"],
  "내 최근 회의 기록": ["My recent meeting history", "Riwayat rapat terbaru saya"],
  "새로고침": ["Refresh", "Muat ulang"],
  "검색": ["Search", "Cari"],
  "Gmail 검색": ["Search Gmail", "Cari Gmail"],
  "연결": ["Link", "Tautkan"],
  "연결 제거": ["Remove link", "Hapus tautan"],
  "오늘": ["Today", "Hari ini"],
  "이전 달": ["Previous month", "Bulan sebelumnya"],
  "다음 달": ["Next month", "Bulan berikutnya"],
  "일정 검색": ["Search events", "Cari acara"],
  "내 캘린더": ["My calendars", "Calendar saya"],
  "현재 업무 태그 일정": ["Events linked to current work item", "Acara tertaut ke item kerja saat ini"],
  "현재 Activity에 태그된 일정": ["Events linked to this Activity", "Acara tertaut ke Activity ini"],
  "업무 Context에 태그됨": ["Linked to work context", "Tertaut ke konteks kerja"],
  "월간 화면에 항상 표시": ["Always shown in the monthly view", "Selalu tampil di tampilan bulanan"],
  "표시 가능한 Calendar 없음": ["No calendars available", "Tidak ada calendar tersedia"],
  "Google Calendar에서 열기": ["Open in Google Calendar", "Buka di Google Calendar"],
  "Meet 참여": ["Join Meet", "Gabung Meet"],
  "참여": ["Join", "Gabung"],
  "열기": ["Open", "Buka"],
  "내 Google 연결": ["Connect My Google", "Hubungkan Google Saya"],
  "내 Google 계정 연결": ["Connect My Google Account", "Hubungkan Akun Google Saya"],
  "내 Google 연결됨": ["My Google is connected", "Google Saya terhubung"],
  "내 Google 연결 필요": ["Connect My Google", "Hubungkan Google Saya"],
  "권한 다시 승인": ["Reauthorize permissions", "Otorisasi ulang izin"],
  "내 Google 연결 해제": ["Disconnect My Google", "Putuskan Google Saya"],
  "권한 확장": ["Expand permissions", "Perluas izin"],
  "Google 권한 다시 승인": ["Reauthorize Google", "Otorisasi ulang Google"],
  "내 Gmail 읽기 권한 필요": ["Gmail read permission required", "Izin baca Gmail diperlukan"],
  "Google Calendar 읽기 권한 필요": ["Google Calendar read permission required", "Izin baca Google Calendar diperlukan"],
  "Meet metadata 권한 필요": ["Meet metadata permission required", "Izin metadata Meet diperlukan"],
  "권한 확장 필요": ["Additional permissions required", "Izin tambahan diperlukan"],
  "파일 이름 검색": ["Search file names", "Cari nama file"],
  "표시할 파일이 없음": ["No files to display", "Tidak ada file untuk ditampilkan"],
  "최근 받은 메일이 없거나 검색 결과가 없음": ["No recent mail or search results", "Tidak ada email terbaru atau hasil pencarian"],
  "최근 Meet 기록을 불러오면 여기에 표시됨": ["Recent Meet history appears here", "Riwayat Meet terbaru akan tampil di sini"],
  "Google에서 열기": ["Open in Google", "Buka di Google"],
  "Calendar에서 열기": ["Open in Calendar", "Buka di Calendar"],
  "크게 보기": ["Expand", "Perbesar"],
  "원래 크기": ["Original size", "Ukuran asli"],
  "내용 보기": ["Content view", "Tampilan isi"],
  "페이지 보기": ["Page view", "Tampilan halaman"],
  "축소": ["Zoom out", "Perkecil"],
  "확대": ["Zoom in", "Perbesar"],
  "100%로 복원": ["Reset to 100%", "Kembali ke 100%"],
  "일정에서 문서·메일·회의를 바로 열어 맥락을 빠르게 확인함.": ["Open documents, mail and meetings directly from the schedule.", "Buka dokumen, email, dan rapat langsung dari jadwal."],
  "이 업무에 공유된 Gmail 없음": ["No Gmail shared with this work item", "Tidak ada Gmail yang dibagikan ke item ini"],
  "링크 연결": ["Link URL", "Tautkan URL"],
  "표시 이름": ["Display name", "Nama tampilan"],
  "Google Docs / Drive 또는 외부 URL": ["Google Docs / Drive or external URL", "Google Docs / Drive atau URL eksternal"]
  ,"NAYSOR Intelligence 추천": ["NAYSOR Intelligence suggestions", "Saran NAYSOR Intelligence"]
  ,"내 Google에서 관련 자료 후보를 찾음": ["Find related candidates in My Google", "Cari kandidat terkait di Google Saya"]
  ,"추천 후보": ["Suggested context", "Konteks yang disarankan"]
  ,"현재 사용자의 Google 자료에서 제목·일정·발신자 메타데이터만 비교함. 연결하기 전에는 팀에 공유되지 않음.": ["Only title, schedule and sender metadata from the current user's Google account is matched. Nothing is shared until you link it.", "Hanya metadata judul, jadwal, dan pengirim dari Google pengguna saat ini yang dicocokkan. Tidak ada yang dibagikan sebelum ditautkan."]
};

function translateGoogleString(value) {
  const language = googleLanguage();
  if (language === "ko") return String(value ?? "");
  let text = String(value ?? "");
  const direct = GOOGLE_INTERFACE[text];
  if (direct) return direct[language === "id" ? 1 : 0];
  const replacements = [
    [/^내 Google · /, language === "id" ? "Google Saya · " : "My Google · "],
    [/^권한 (\d+)개 확장 필요$/, language === "id" ? "Perlu $1 izin tambahan" : "$1 additional permissions required"],
    [/^이 업무에 연결된 메일 (\d+)개$/, language === "id" ? "$1 email tertaut ke item ini" : "$1 emails linked to this work item"],
    [/^\+(\d+)개 더보기$/, language === "id" ? "+$1 lainnya" : "+$1 more"],
    [/^현재 (.+)에 일정 태그$/, language === "id" ? "Tautkan acara ke $1" : "Link event to $1"],
    [/^연결: /, language === "id" ? "Ditautkan oleh: " : "Linked by: "]
  ];
  for (const [pattern, replacement] of replacements) text = text.replace(pattern, replacement);
  return text;
}

function translateGoogleInterface(root) {
  if (!root || googleLanguage() === "ko") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(node => {
    if (node.parentElement?.closest("script,style")) return;
    const original = node.nodeValue;
    const core = original.trim();
    if (!core) return;
    const translated = translateGoogleString(core);
    if (translated !== core) node.nodeValue = original.replace(core, translated);
  });
  root.querySelectorAll?.("[placeholder]").forEach(element => { element.placeholder = translateGoogleString(element.placeholder); });
  root.querySelectorAll?.("[title]").forEach(element => { element.title = translateGoogleString(element.title); });
  root.querySelectorAll?.("[aria-label]").forEach(element => { element.setAttribute("aria-label", translateGoogleString(element.getAttribute("aria-label"))); });
}

function icon(kind) {
  const glyphs = {
    folder: '<path d="M3 6.5h7l2 2h9v10.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M3 9h18"/>',
    doc: '<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h5M9 12h6M9 16h6"/>',
    sheet: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h8M12 8v8"/>',
    slide: '<rect x="3" y="4" width="18" height="14" rx="2"/><path d="M8 21h8M12 18v3M7 8h10v6H7z"/>',
    pdf: '<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h5M9 13h6M9 17h4"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="m4 18 5-5 3 3 2-2 6 5"/>',
    calendar: '<g stroke="none"><path fill="#4285f4" d="M3 8h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path fill="#34a853" d="M3 8h5v13H5a2 2 0 0 1-2-2z"/><path fill="#fbbc04" d="M16 8h5v11a2 2 0 0 1-2 2h-3z"/><path fill="#ea4335" d="M5 3h14a2 2 0 0 1 2 2v4H3V5a2 2 0 0 1 2-2z"/><text x="12" y="18" text-anchor="middle" font-size="8" font-weight="800" fill="#fff">31</text></g>',
    drive: '<g stroke="none"><path fill="#4285f4" d="M8.2 3h7.6l5.4 9.3-2.7 4.7H5.5l-2.7-4.7z"/><path fill="#34a853" d="m8.2 3 5.4 9.3H21L15.8 3z"/><path fill="#fbbc04" d="M2.8 12.3 5.5 17h13l-2.7-4.7z"/></g>',
    mail: '<g stroke="none"><path fill="#4285f4" d="M3 6.5 12 13l9-6.5V19H3z"/><path fill="#ea4335" d="M3 6.5 12 13l2-1.45L5 5H4a1 1 0 0 0-1 1.5z"/><path fill="#fbbc04" d="M21 6.5 12 13l-2-1.45L19 5h1a1 1 0 0 1 1 1.5z"/><path fill="#34a853" d="M3 18.8V8.5l4 2.9V19H4a1 1 0 0 1-1-.2z"/></g>',
    meet: '<g stroke="none"><path fill="#00ac47" d="M3 7a2 2 0 0 1 2-2h10v14H5a2 2 0 0 1-2-2z"/><path fill="#00832d" d="M15 9.2 21 6v12l-6-3.2z"/><path fill="#ffba00" d="M3 7h4v12H5a2 2 0 0 1-2-2z"/><path fill="#2684fc" d="M7 5h8v14H7z"/></g>',
    link: '<path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.2 1.2"/><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.2-1.2"/>',
    file: '<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h5"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
    refresh: '<path d="M20 6v5h-5"/><path d="M18.5 15a7 7 0 1 1-.3-7.5L20 11"/>',
    external: '<path d="M14 4h6v6M20 4l-9 9"/><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/>'
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${glyphs[kind] || glyphs.file}</svg>`;
}

function normalizeGoogleFile(file = {}) {
  const kind = googleFileKind(file.mimeType || "", file.provider || "google_drive");
  return {
    ...file,
    kind,
    provider: file.provider || "google_drive",
    externalId: file.externalId || file.id || "",
    previewUrl: file.previewUrl || googlePreviewUrl(file)
  };
}

function inferManualLink(url, title = "") {
  const text = String(url || "").trim();
  let provider = "link";
  let mimeType = "text/uri-list";
  let externalId = "";
  let kind = "link";
  const patterns = [
    [/docs\.google\.com\/document\/d\/([^/]+)/i, "google_drive", "application/vnd.google-apps.document", "doc"],
    [/docs\.google\.com\/spreadsheets\/d\/([^/]+)/i, "google_drive", "application/vnd.google-apps.spreadsheet", "sheet"],
    [/docs\.google\.com\/presentation\/d\/([^/]+)/i, "google_drive", "application/vnd.google-apps.presentation", "slide"],
    [/drive\.google\.com\/(?:file\/d\/|drive\/folders\/)([^/?]+)/i, "google_drive", "application/octet-stream", "file"],
    [/meet\.google\.com\/([a-z-]+)/i, "google_meet", "application/vnd.google-apps.meet", "meet"]
  ];
  for (const [pattern, matchProvider, mime, fileKind] of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    provider = matchProvider;
    mimeType = mime;
    kind = fileKind;
    externalId = match[1];
    break;
  }
  const file = { id: externalId, externalId, name: title || "연결 자료", title: title || "연결 자료", url: text, webViewLink: text, provider, mimeType, kind };
  file.previewUrl = provider === "google_drive" ? googlePreviewUrl(file) : text;
  return file;
}

function eventDateText(event = {}) {
  const start = event.start?.dateTime || event.start?.date || event.metadata?.start?.dateTime || event.metadata?.start?.date || "";
  const end = event.end?.dateTime || event.end?.date || event.metadata?.end?.dateTime || event.metadata?.end?.date || "";
  const format = value => {
    if (!value) return "—";
    const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString(googleLocale(), { month: "2-digit", day: "2-digit", hour: value.length === 10 ? undefined : "2-digit", minute: value.length === 10 ? undefined : "2-digit" });
  };
  return `${format(start)}${end && end !== start ? ` → ${format(end)}` : ""}`;
}

function meetUrlOf(event = {}) {
  return event.hangoutLink || event.metadata?.hangoutLink || event.metadata?.conferenceData?.entryPoints?.find?.(item => item?.entryPointType === "video")?.uri || "";
}

function attachmentKind(item = {}) {
  return googleFileKind(item.mimeType || "", item.provider || "");
}

function providerLabel(item = {}) {
  const kind = attachmentKind(item);
  return ({ doc: "Google Docs", sheet: "Google Sheets", slide: "Google Slides", pdf: "PDF", image: "Image", mail: "Gmail", calendar: "Google Calendar", meet: "Google Meet", folder: "Google Drive", file: item.provider === "google_drive" ? "Google Drive" : "Link" })[kind] || "Link";
}

class GoogleWorkspaceUI {
  constructor(platform) {
    this.platform = platform;
    this.service = platform.mode === "cloud" ? new GoogleWorkspaceService(platform) : null;
    this.connection = { connected: false, settings: null, capabilities: {} };
    this.drives = [];
    this.files = [];
    this.calendars = [];
    this.calendarEvents = [];
    this.emails = [];
    this.meetRecords = [];
    this.driveId = "";
    this.folderId = "";
    this.folderTrail = [];
    this.search = "";
    this.gmailQuery = "in:inbox newer_than:90d";
    this.recommendations = [];
    this.recommendationMap = new Map();
    this.recommendationsLoaded = false;
    this.recommendationLoading = false;
    this.calendarQuery = "";
    this.calendarMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    this.calendarSelectedIds = new Set();
    this.selectedCalendarEventKey = "";
    this.activeTab = "context";
    this.entity = { type: "program", id: "program" };
    this.preview = null;
    this.previewAssetUrl = "";
    this.previewAssetType = "";
    this.previewEmbedUrl = "";
    this.previewRelay = null;
    this.previewOfficeBuffer = null;
    this.previewOfficeMime = "";
    this.previewOfficeObjectUrls = [];
    this.previewOfficeRenderToken = 0;
    this.previewError = "";
    this.previewLoading = false;
    this.previewDetails = null;
    this.previewNative = null;
    this.previewSlideIndex = 0;
    this.previewSlideUrl = "";
    this.previewSlideLoading = false;
    this.previewSlideUrls = [];
    this.previewSlideBatchLoading = false;
    this.previewExpanded = false;
    this.previewZoom = 1;
    this.previewMode = "layout";
    this.previewCache = new Map();
    this.loading = false;
    this.observer = null;
    this.root = null;
  }

  get state() { return window.NAYSOR_APP?.getState?.() || this.platform.state || {}; }
  get openButton() { return document.getElementById("googleWorkspaceButton"); }

  async start() {
    this.mount();
    this.bind();
    this.syncQueryState();
    await this.refreshStatus();
    if (this.platform.mode === "cloud" && !this.connection.connected && !sessionStorage.getItem("naysor-google-personal-onboarding-v29")) {
      sessionStorage.setItem("naysor-google-personal-onboarding-v29", "1");
      this.activeTab = "settings";
      this.open();
    }
    this.decorateEntities();
    this.observer = new MutationObserver(records => {
      const structural = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => {
        if (!(node instanceof Element)) return true;
        return !node.matches("[data-google-generated='true'],.collab-editor-badge");
      }));
      if (structural) this.decorateEntities();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("naysor:state-reloaded", () => { this.render(); this.decorateEntities(); });
    window.addEventListener("naysor:language-changed", () => { this.render(); this.decorateEntities(); });
    window.NAYSOR_GOOGLE = this;
  }

  mount() {
    let root = document.getElementById("googleWorkspaceRoot");
    if (!root) {
      root = document.createElement("div");
      root.id = "googleWorkspaceRoot";
      document.body.appendChild(root);
    }
    this.root = root;
    root.innerHTML = `
      <div class="workspace-drawer-backdrop" data-google-action="close" hidden></div>
      <aside class="workspace-drawer liquid-surface" aria-label="Google Workspace 연결" aria-hidden="true">
        <header class="workspace-drawer-header">
          <div class="workspace-brand-icon">${icon("drive")}</div>
          <div><span class="eyebrow">CONNECTED WORKSPACE</span><h2>Google Project Context</h2><p id="googleConnectionText">연결 상태 확인 중</p></div>
          <button class="icon-button" type="button" data-google-action="close" aria-label="닫기">×</button>
        </header>
        <nav class="workspace-tabs" aria-label="Google Workspace 메뉴">
          <button class="active" data-google-tab="context">${icon("link")} Context</button>
          <button data-google-tab="drive">${icon("drive")} 내 Drive</button>
          <button data-google-tab="calendar">${icon("calendar")} 내 Calendar · Meet</button>
          <button data-google-tab="mail">${icon("mail")} 내 Gmail</button>
          <button data-google-tab="meet">${icon("meet")} 내 Meet 기록</button>
          <button data-google-tab="settings">${icon("link")} 내 연결</button>
        </nav>
        <div class="workspace-drawer-content" id="googleWorkspaceContent"></div>
      </aside>`;
    this.render();
  }

  bind() {
    this.openButton?.addEventListener("click", () => this.open());
    this.root.addEventListener("click", event => this.onClick(event));
    this.root.addEventListener("change", event => this.onChange(event));
    this.root.addEventListener("submit", event => this.onSubmit(event));
    document.addEventListener("click", event => {
      const indicator = event.target.closest("[data-google-entity-type]");
      if (!indicator) return;
      event.preventDefault();
      event.stopPropagation();
      this.entity = { type: indicator.dataset.googleEntityType, id: indicator.dataset.googleEntityId };
      this.recommendations = [];
      this.recommendationMap.clear();
      this.recommendationsLoaded = false;
      this.activeTab = indicator.dataset.googleOpenTab || "context";
      this.preview = null;
      this.releasePreviewAsset();
      this.open();
    }, true);
  }

  syncQueryState() {
    const url = new URL(window.location.href);
    const result = url.searchParams.get("google");
    const googleEmail = url.searchParams.get("googleEmail") || "";
    const googleReason = url.searchParams.get("googleReason") || "";
    if (!result) return;
    url.searchParams.delete("google");
    url.searchParams.delete("googleEmail");
    url.searchParams.delete("googleReason");
    history.replaceState({}, "", url);
    const message = result === "connected" ? "내 Google 계정 연결 완료"
      : result === "access_denied" ? "Google OAuth 접근이 차단됨 · Google Cloud의 External/Test user 또는 Production 설정을 확인해야 함"
      : result === "cancelled" ? "Google 연결이 취소됨"
      : result === "google_account_unverified" ? `선택한 Google 계정의 이메일을 확인할 수 없음${googleEmail ? ` · ${googleEmail}` : ""}`
      : googleReason ? `Google 연결 오류 · ${googleReason}` : "Google 연결 확인 필요";
    window.dispatchEvent(new CustomEvent("naysor:toast", { detail: message }));
  }

  open() {
    this.root.querySelector(".workspace-drawer-backdrop").hidden = false;
    const drawer = this.root.querySelector(".workspace-drawer");
    drawer.classList.add("open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("workspace-drawer-open");
    this.render();
  }

  close() {
    this.root.querySelector(".workspace-drawer-backdrop").hidden = true;
    const drawer = this.root.querySelector(".workspace-drawer");
    drawer.classList.remove("open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("workspace-drawer-open");
  }

  async refreshStatus() {
    if (!this.service) {
      this.connection = { connected: false, demo: true, settings: null, capabilities: {} };
      this.updateButton();
      this.render();
      return;
    }
    try {
      this.connection = await this.service.status();
    } catch (error) {
      this.connection = { connected: false, error: error.message, settings: null, capabilities: {} };
    }
    this.updateButton();
    this.render();
  }

  async maybeAutoSync() {
    // v2.9: Google Calendar is context-only. It must never create/update CPM activities.
    return;
  }

  updateButton() {
    if (!this.openButton) return;
    this.openButton.classList.toggle("connected", Boolean(this.connection.connected));
    this.openButton.classList.toggle("error", Boolean(this.connection.error || this.connection.lastError || this.connection.missingScopes?.length));
    this.openButton.querySelector(".google-workspace-status")?.replaceChildren(document.createTextNode(this.connection.connected ? "연결됨" : "Workspace"));
  }

  entityGroups() {
    const s = this.state;
    const group = (type, label, items, name) => ({ type, label, items: (items || []).map(item => ({ id: item.id, label: name(item) })) });
    return [
      { type: "program", label: "Program", items: [{ id: "program", label: `${s.meta?.programCode || "PRG-01"} ${s.meta?.programName || "NAYSOR Program"}` }] },
      group("project", "Project", s.projects, item => `${item.code} ${item.name}`),
      group("phase", "Phase", s.phases, item => `${item.code} ${item.koreanName || item.name}`),
      group("activity", "Activity", s.activities, item => `${item.id} ${item.name}`),
      group("organization", "Organization", s.organizations, item => `${item.id} ${item.name}`),
      group("gate", "Gate", s.gates, item => `${item.code} ${item.name}`),
      group("deliverable", "Deliverable", s.deliverables, item => `${item.code || item.id} ${item.name}`),
      group("agreement", "MOU / Action", s.agreements, item => `${item.id} ${item.name}`),
      group("risk", "Risk", s.risks, item => `${item.id} ${item.description}`)
    ];
  }

  entityOptions() {
    return this.entityGroups().map(group => `<optgroup label="${esc(group.label)}">${group.items.map(item => `<option value="${group.type}:${esc(item.id)}" ${group.type === this.entity.type && item.id === this.entity.id ? "selected" : ""}>${esc(item.label)}</option>`).join("")}</optgroup>`).join("");
  }

  currentAttachments() {
    return (this.state.attachments || []).filter(item => item.entityType === this.entity.type && item.entityId === this.entity.id);
  }

  currentTaggedCalendarAttachments() {
    return this.currentAttachments().filter(item => attachmentKind(item) === "calendar");
  }

  taggedCalendarEvents() {
    return this.currentTaggedCalendarAttachments().map(attachment => {
      const metadata = attachment.metadata || {};
      const [externalCalendarId = "", externalEventId = ""] = String(attachment.externalId || "").split("::");
      return {
        ...metadata,
        id: metadata.id || externalEventId || String(attachment.id),
        calendarId: metadata.calendarId || externalCalendarId || `naysor-context-${attachment.sourceProfileId || "shared"}`,
        summary: metadata.summary || attachment.title || "Google Calendar Event",
        htmlLink: metadata.htmlLink || attachment.webViewLink || attachment.url || "",
        calendarSummary: metadata.calendarSummary || "NAYSOR Project Context",
        calendarColor: metadata.calendarColor || "#0f9d58",
        sourceGoogleEmail: attachment.sourceGoogleEmail || metadata.sourceGoogleEmail || "",
        _naysorTagged: true,
        _attachmentId: attachment.id
      };
    }).filter(event => event.start?.dateTime || event.start?.date);
  }

  calendarDisplayEvents() {
    const merged = new Map(this.calendarEvents.map(event => [this.eventKey(event), { ...event }]));
    for (const tagged of this.taggedCalendarEvents()) {
      const key = this.eventKey(tagged);
      const existing = merged.get(key);
      merged.set(key, existing ? { ...existing, _naysorTagged: true, _attachmentId: tagged._attachmentId, sourceGoogleEmail: tagged.sourceGoogleEmail || existing.sourceGoogleEmail } : tagged);
    }
    return [...merged.values()];
  }

  currentCalendarLinks() {
    // v2.9+: legacy Calendar→Gantt sync links are intentionally ignored.
    // Calendar events shared with a Phase/Activity live in attachments(provider=google_calendar).
    return [];
  }

  currentEntityLabel() {
    for (const group of this.entityGroups()) {
      if (group.type !== this.entity.type) continue;
      return group.items.find(item => item.id === this.entity.id)?.label || `${this.entity.type} ${this.entity.id}`;
    }
    return `${this.entity.type} ${this.entity.id}`;
  }

  currentEntityRecord() {
    if (this.entity.type === "program") return this.state.meta || {};
    const collections = {
      project: "projects", phase: "phases", activity: "activities", organization: "organizations",
      gate: "gates", deliverable: "deliverables", agreement: "agreements", risk: "risks"
    };
    const items = this.state[collections[this.entity.type]] || [];
    return items.find(item => String(item.id) === String(this.entity.id)) || {};
  }

  recommendationSeed() {
    const record = this.currentEntityRecord();
    const text = [
      record.code, record.name, record.koreanName, record.description, record.organization,
      record.source, record.notes, record.kpi, record.objective, record.scope
    ].filter(Boolean).join(" ");
    const preferred = [
      /PT\.?\s*SATU/gi, /SJ\s*NEXT/gi, /Satria\s*Nusa/gi, /Growth\s*Steel/gi,
      /Bluepoint/gi, /Menthobi/gi, /UNILA/gi, /BRIN/gi, /KIER/gi, /TIPS/gi,
      /TRL/gi, /PFAD/gi, /Lampung/gi, /F-?COMB/gi, /POME/gi, /EFB/gi
    ];
    const terms = [];
    const add = value => {
      const term = String(value || "").replace(/\s+/g, " ").trim();
      if (term.length < 3 || /^NAYSOR$/i.test(term) || terms.some(item => item.toLowerCase() === term.toLowerCase())) return;
      terms.push(term);
    };
    preferred.forEach(pattern => [...text.matchAll(pattern)].forEach(match => add(match[0])));
    String(record.organization || "").split(/\s*\/\s*|\s*·\s*|\s*,\s*/).forEach(add);
    String(record.name || "").match(/[A-Za-z][A-Za-z0-9._-]{2,}|[가-힣]{2,}/g)?.forEach(add);
    const start = record.start || record.date || record.createdAt || "";
    const finish = record.finish || record.deadline || record.due || record.targetDate || start;
    return { record, terms: terms.slice(0, 6), start, finish };
  }

  recommendationKey(item = {}) {
    return `${item.provider || "link"}:${item.externalId || item.id || item.name || item.title || "resource"}`;
  }

  recommendationScore(item = {}, terms = []) {
    const metadata = item.metadata || {};
    const haystack = [
      item.name, item.title, item.mimeType, metadata.summary, metadata.subject, metadata.from,
      metadata.to, metadata.description, metadata.snippet, metadata.organizer?.email,
      metadata.calendarSummary, metadata.meetingCode
    ].filter(Boolean).join(" ").toLowerCase();
    return terms.reduce((score, term, index) => score + (haystack.includes(String(term).toLowerCase()) ? Math.max(2, 10 - index) : 0), 0);
  }

  async recommendContext() {
    if (!this.service || !this.connection.connected) return this.toast("내 Google 계정을 먼저 연결하세요.");
    const seed = this.recommendationSeed();
    if (!seed.terms.length) return this.toast("이 항목에서 추천 검색어를 만들 수 없음");
    this.recommendationLoading = true;
    this.recommendationsLoaded = true;
    this.render();
    try {
      const candidates = [];
      const driveTerms = seed.terms.slice(0, 4);
      const driveResults = await Promise.allSettled(driveTerms.map(search => this.service.files({ search })));
      driveResults.forEach(result => {
        if (result.status !== "fulfilled") return;
        (result.value.files || []).forEach(file => {
          const normalized = normalizeGoogleFile(file);
          if (attachmentKind(normalized) !== "folder") candidates.push(normalized);
        });
      });

      const tasks = [];
      if (this.connection.capabilities?.gmail) {
        const query = `newer_than:5y {${seed.terms.slice(0, 5).map(term => `\"${String(term).replace(/\"/g, "")}\"`).join(" ")}}`;
        tasks.push(this.service.gmailSearch(query, 40).then(result => (result.messages || []).map(item => this.gmailAttachment(item))));
      }
      if (this.connection.capabilities?.calendar) {
        if (!this.calendars.length) this.calendars = (await this.service.calendars()).calendars || [];
        const from = seed.start ? new Date(`${String(seed.start).slice(0, 10)}T00:00:00`) : new Date(Date.now() - 365 * 86400000);
        const to = seed.finish ? new Date(`${String(seed.finish).slice(0, 10)}T23:59:59`) : new Date();
        if (Number.isNaN(from.getTime())) from.setTime(Date.now() - 365 * 86400000);
        if (Number.isNaN(to.getTime())) to.setTime(Date.now());
        from.setDate(from.getDate() - 45);
        to.setDate(to.getDate() + 45);
        tasks.push(this.service.calendarEventsBatch({
          calendarIds: this.calendars.slice(0, 20).map(calendar => calendar.id),
          timeMin: from.toISOString(), timeMax: to.toISOString(), maxResults: 250
        }).then(result => (result.events || []).map(item => this.calendarAttachment(item))));
      }
      if (this.connection.capabilities?.meet) tasks.push(this.service.meetRecords(40).then(result => (result.records || []).map(item => this.meetAttachment(item))));
      const contextualResults = await Promise.allSettled(tasks);
      contextualResults.forEach(result => { if (result.status === "fulfilled") candidates.push(...result.value); });

      const linked = new Set(this.currentAttachments().map(item => this.recommendationKey(item)));
      const ranked = new Map();
      candidates.filter(Boolean).forEach(item => {
        const key = this.recommendationKey(item);
        if (linked.has(key)) return;
        const score = this.recommendationScore(item, seed.terms);
        if (!score || (ranked.has(key) && ranked.get(key).score >= score)) return;
        ranked.set(key, { key, item, score });
      });
      this.recommendations = [...ranked.values()].sort((a, b) => b.score - a.score || String(a.item.title || a.item.name).localeCompare(String(b.item.title || b.item.name))).slice(0, 18);
      this.recommendationMap = new Map(this.recommendations.map(entry => [entry.key, entry.item]));
      this.toast(this.recommendations.length ? `관련 Context 후보 ${this.recommendations.length}개를 찾음` : "관련 후보를 찾지 못함");
    } catch (error) {
      this.toast(error.message || String(error));
    } finally {
      this.recommendationLoading = false;
      this.render();
    }
  }

  renderRecommendations() {
    if (!this.recommendationsLoaded) return "";
    if (this.recommendationLoading) return '<div class="context-recommendations"><div class="workspace-empty compact">NAYSOR Intelligence가 내 Google 자료의 메타데이터를 비교하는 중…</div></div>';
    const cards = this.recommendations.map(entry => {
      const item = entry.item;
      const kind = attachmentKind(item);
      const subtitle = kind === "calendar" ? eventDateText(item.metadata || {})
        : kind === "mail" ? `${item.metadata?.from || "Gmail"}${item.metadata?.date ? ` · ${item.metadata.date}` : ""}`
        : providerLabel(item);
      return `<article class="context-recommendation-card">
        <button type="button" class="context-recommendation-main" data-google-action="preview-recommendation" data-recommendation-key="${esc(entry.key)}">
          <span class="workspace-file-icon ${kind}">${icon(kind)}</span>
          <span><strong>${esc(item.title || item.name || "Google Context")}</strong><small>${esc(subtitle)} · relevance ${entry.score}</small></span>
        </button>
        <button class="button small" type="button" data-google-action="attach-recommendation" data-recommendation-key="${esc(entry.key)}">연결</button>
      </article>`;
    }).join("") || '<div class="workspace-empty compact">추천 가능한 관련 자료가 없음. Drive·Calendar·Gmail에서 직접 검색할 수 있음.</div>';
    return `<section class="context-recommendations"><div class="workspace-section-title"><div><span class="eyebrow">NAYSOR INTELLIGENCE</span><h3>추천 후보</h3><p>현재 사용자의 Google 자료에서 제목·일정·발신자 메타데이터만 비교함. 연결하기 전에는 팀에 공유되지 않음.</p></div><span class="resource-count">${this.recommendations.length}</span></div><div class="context-recommendation-grid">${cards}</div></section>`;
  }

  render() {
    if (!this.root) return;
    const drawer = this.root.querySelector(".workspace-drawer");
    drawer?.classList.toggle("calendar-expanded", this.activeTab === "calendar" && !this.previewExpanded);
    const text = document.getElementById("googleConnectionText");
    if (text) {
      const count = this.connection.missingScopes?.length || 0;
      const missing = count ? ` · ${googleLanguage() === "id" ? `Perlu ${count} izin tambahan` : googleLanguage() === "en" ? `${count} additional permissions required` : `권한 ${count}개 확장 필요`}` : "";
      text.textContent = this.connection.connected
        ? `${googleLanguage() === "id" ? "Google Saya" : googleLanguage() === "en" ? "My Google" : "내 Google"} · ${this.connection.email || "Google Workspace"}${missing}`
        : this.connection.demo
          ? (googleLanguage() === "id" ? "Mode demo · hanya tautan manual" : googleLanguage() === "en" ? "Demo mode · manual links only" : "Demo 모드 · 수동 링크만 사용 가능")
          : this.connection.error || this.connection.lastError || (googleLanguage() === "id" ? "Hubungkan akun Google" : googleLanguage() === "en" ? "Connect a Google account" : "Google 계정 연결 필요");
    }
    const tabNames = {
      context: ["Context", "Context", "Konteks"],
      drive: ["내 Drive", "My Drive", "Drive Saya"],
      calendar: ["내 Calendar · Meet", "My Calendar · Meet", "Calendar · Meet Saya"],
      mail: ["내 Gmail", "My Gmail", "Gmail Saya"],
      meet: ["내 Meet 기록", "My Meet History", "Riwayat Meet Saya"],
      settings: ["내 연결", "My Connection", "Koneksi Saya"]
    };
    const tabIcons = { context: "link", drive: "drive", calendar: "calendar", mail: "mail", meet: "meet", settings: "link" };
    const languageIndex = googleLanguage() === "id" ? 2 : googleLanguage() === "en" ? 1 : 0;
    this.root.querySelectorAll("[data-google-tab]").forEach(button => {
      button.classList.toggle("active", button.dataset.googleTab === this.activeTab);
      button.innerHTML = `${icon(tabIcons[button.dataset.googleTab] || "link")} ${tabNames[button.dataset.googleTab]?.[languageIndex] || button.dataset.googleTab}`;
    });
    const content = document.getElementById("googleWorkspaceContent");
    if (!content) return;
    if (this.activeTab === "drive") content.innerHTML = this.renderDrive();
    else if (this.activeTab === "calendar") content.innerHTML = this.renderCalendar();
    else if (this.activeTab === "mail") content.innerHTML = this.renderMail();
    else if (this.activeTab === "meet") content.innerHTML = this.renderMeet();
    else if (this.activeTab === "settings") content.innerHTML = this.renderSettings();
    else content.innerHTML = this.renderContext();
    translateGoogleInterface(this.root);
    if (this.previewOfficeBuffer) requestAnimationFrame(() => this.hydrateOfficePreview());
  }

  async hydrateOfficePreview() {
    const host = this.root?.querySelector("[data-office-preview]");
    if (!host || !this.previewOfficeBuffer) return;
    const token = ++this.previewOfficeRenderToken;
    for (const url of this.previewOfficeObjectUrls.splice(0)) URL.revokeObjectURL(url);
    host.innerHTML = '<div class="preview-loader compact"><span></span><strong>NAYSOR 자체 문서 Viewer 렌더링 중…</strong><small>Google iframe을 사용하지 않음</small></div>';
    try {
      await renderOfficePreview(this.previewOfficeBuffer, this.previewOfficeMime, host, {
        zoom: this.previewZoom || 1,
        registerObjectUrl: url => this.previewOfficeObjectUrls.push(url)
      });
      if (token !== this.previewOfficeRenderToken) return;
    } catch (error) {
      if (token !== this.previewOfficeRenderToken) return;
      host.innerHTML = `<div class="workspace-empty preview-error"><div><strong>NAYSOR 자체 Preview 렌더링 실패</strong><p>${esc(error?.message || String(error))}</p></div></div>`;
    }
  }

  renderEntityPicker() {
    return `<label class="workspace-entity-picker"><span>연결 대상</span><select data-google-field="entity">${this.entityOptions()}</select></label>`;
  }

  renderResourceCard(item) {
    const kind = attachmentKind(item);
    const sourceEmail = item.sourceGoogleEmail || item.metadata?.sourceGoogleEmail || "";
    const baseSub = kind === "mail" ? `${item.metadata?.from || "Gmail"}${item.metadata?.date ? ` · ${item.metadata.date}` : ""}`
      : kind === "calendar" ? eventDateText(item.metadata || {})
      : kind === "meet" ? `${item.metadata?.startTime ? new Date(item.metadata.startTime).toLocaleString("ko-KR") : "Meet"}`
      : `${providerLabel(item)}${item.modifiedTime ? ` · ${new Date(item.modifiedTime).toLocaleDateString("ko-KR")}` : ""}`;
    const sub = `${baseSub}${sourceEmail ? ` · 연결: ${sourceEmail}` : ""}`;
    const open = item.webViewLink || item.url || item.metadata?.htmlLink || item.metadata?.meetingUri || "";
    const snapshot=item.snapshotStatus==="ready"?'<em class="snapshot-badge ready">SNAPSHOT</em>':item.snapshotStatus==="failed"?'<em class="snapshot-badge failed" title="Snapshot 보존 실패">LIVE ONLY</em>':'<em class="snapshot-badge pending">ARCHIVING</em>';
    return `<article class="linked-resource-card resource-${kind}">
      <button class="linked-resource-main" type="button" data-google-action="preview-attachment" data-attachment-id="${esc(item.id)}">
        <span class="workspace-file-icon ${kind}">${item.iconLink ? `<img src="${esc(item.iconLink)}" alt="">` : icon(kind)}</span>
        <span><strong>${esc(item.title || item.name || "연결 자료")} ${snapshot}</strong><small>${esc(sub)}</small></span>
      </button>
      ${meetUrlOf(item.metadata || {}) ? `<a class="resource-quick-action meet" href="${esc(meetUrlOf(item.metadata || {}))}" target="_blank" rel="noopener">${icon("meet")} 참여</a>` : ""}
      ${open ? `<a class="icon-button" href="${esc(open)}" target="_blank" rel="noopener" title="Google에서 열기">↗</a>` : ""}
      <button class="icon-button danger-text" type="button" data-google-action="delete-attachment" data-attachment-id="${esc(item.id)}" title="연결 제거">×</button>
    </article>`;
  }

  renderSyncedEvent(link) {
    const event = link.snapshot || {};
    const meet = meetUrlOf(event);
    return `<article class="linked-resource-card resource-calendar">
      <button class="linked-resource-main" type="button" data-google-action="preview-sync-event" data-calendar-link-id="${esc(link.id)}">
        <span class="workspace-file-icon calendar">${icon("calendar")}</span>
        <span><strong>${esc(event.summary || "Google Calendar Event")}</strong><small>${esc(eventDateText(event))}${link.sourceGoogleEmail ? ` · 연결: ${esc(link.sourceGoogleEmail)}` : ""}</small></span>
      </button>
      ${meet ? `<a class="resource-quick-action meet" href="${esc(meet)}" target="_blank" rel="noopener">${icon("meet")} Meet</a>` : ""}
      ${link.url ? `<a class="icon-button" href="${esc(link.url)}" target="_blank" rel="noopener" title="Calendar에서 열기">↗</a>` : ""}
    </article>`;
  }

  renderContext() {
    const attachments = this.currentAttachments();
    const calendarLinks = this.currentCalendarLinks();
    const counts = { docs: 0, mail: 0, calendar: calendarLinks.length, meet: calendarLinks.filter(link => meetUrlOf(link.snapshot || {})).length };
    attachments.forEach(item => {
      const kind = attachmentKind(item);
      if (["doc", "sheet", "slide", "pdf", "image", "file"].includes(kind)) counts.docs += 1;
      if (kind === "mail") counts.mail += 1;
      if (kind === "calendar") counts.calendar += 1;
      if (kind === "meet" || meetUrlOf(item.metadata || {})) counts.meet += 1;
    });
    const resources = attachments.map(item => this.renderResourceCard(item)).join("") || '<div class="workspace-empty compact">이 항목에 연결된 문서·메일·일정이 없음</div>';
    const synced = calendarLinks.map(link => this.renderSyncedEvent(link)).join("");
    return `<section class="workspace-section context-hub">
      ${this.renderEntityPicker()}
      <div class="context-hero">
        <div><span class="eyebrow">PROJECT CONTEXT</span><h3>${esc(this.currentEntityLabel())}</h3><p>일정에서 문서·메일·회의를 바로 열어 맥락을 빠르게 확인함.</p><div class="context-hero-actions"><button class="button secondary small" type="button" data-google-action="recommend-context">NAYSOR Intelligence 추천</button><small>내 Google에서 관련 자료 후보를 찾음</small></div></div>
        <div class="context-metrics">
          <button type="button" data-google-tab-jump="drive"><span>${icon("drive")}</span><strong>${counts.docs}</strong><small>Files</small></button>
          <button type="button" data-google-tab-jump="mail"><span>${icon("mail")}</span><strong>${counts.mail}</strong><small>Mail</small></button>
          <button type="button" data-google-tab-jump="calendar"><span>${icon("calendar")}</span><strong>${counts.calendar}</strong><small>Events</small></button>
          <button type="button" data-google-tab-jump="meet"><span>${icon("meet")}</span><strong>${counts.meet}</strong><small>Meet</small></button>
        </div>
      </div>
      ${this.renderRecommendations()}
      <div class="workspace-section-title"><div><span class="eyebrow">LINKED CONTEXT</span><h3>연결 자료</h3></div><span class="resource-count">${attachments.length + calendarLinks.length}</span></div>
      <div class="workspace-linked-section">${resources}${synced}</div>
      ${this.renderPreview()}
    </section>`;
  }

  renderPersonalGoogleBanner(serviceName, description = "") {
    const email = this.connection.email || this.connection.pmEmail || "Google 계정 미연결";
    return `<div class="personal-google-banner"><span class="personal-google-badge">PERSONAL GOOGLE</span><div><strong>${esc(serviceName)} · ${esc(email)}</strong><p>${esc(description || "이 탭은 현재 PM 로그인 사용자가 직접 연결한 Google 계정의 개인 영역임. 여기서 명시적으로 연결한 항목만 Project Context로 공유됨.")}</p></div></div>`;
  }

  renderDrive() {
    const attachments = this.currentAttachments().filter(item => !["mail", "calendar", "meet"].includes(attachmentKind(item)));
    const driveOptions = `<option value="">내 드라이브</option>${this.drives.map(d => `<option value="${esc(d.id)}" ${d.id === this.driveId ? "selected" : ""}>${esc(d.name)}</option>`).join("")}`;
    const files = this.files.map(file => {
      const kind = googleFileKind(file.mimeType);
      const isFolder = kind === "folder";
      return `<article class="workspace-file-card" data-file-id="${esc(file.id)}">
        <button class="workspace-file-main" type="button" data-google-action="${isFolder ? "open-folder" : "preview-file"}" data-file-id="${esc(file.id)}">
          <span class="workspace-file-icon ${kind}">${file.iconLink ? `<img src="${esc(file.iconLink)}" alt="">` : icon(kind)}</span>
          <span><strong>${esc(file.name)}</strong><small>${isFolder ? "폴더" : esc(file.modifiedTime ? new Date(file.modifiedTime).toLocaleString("ko-KR") : file.mimeType || "파일")}</small></span>
        </button>
        ${isFolder ? "" : `<button class="button small" type="button" data-google-action="attach-file" data-file-id="${esc(file.id)}">연결</button>`}
      </article>`;
    }).join("") || `<div class="workspace-empty">${this.connection.connected ? "표시할 파일이 없음" : "내 Google 계정을 연결하면 내 Drive와 내가 접근 가능한 공유 드라이브를 탐색할 수 있음"}</div>`;
    const links = attachments.map(item => this.renderResourceCard(item)).join("") || '<div class="workspace-empty compact">이 항목에 연결된 Google 자료가 없음</div>';
    return `<section class="workspace-section">
      ${this.renderEntityPicker()}
      ${this.renderPersonalGoogleBanner("내 Drive", "현재 로그인 사용자의 Google Drive/공유 드라이브만 탐색함. 파일을 ‘연결’하면 그 시점부터 NAYSOR PM의 공유 Context가 됨.")}
      <div class="workspace-linked-section"><div class="workspace-section-title"><div><span class="eyebrow">LINKED FILES</span><h3>현재 연결 문서</h3></div><span class="resource-count">${attachments.length}</span></div>${links}</div>
      <form class="manual-link-form" data-google-form="manual-link"><div class="field"><label>Google Docs / Drive 또는 외부 URL</label><input name="url" type="url" placeholder="https://docs.google.com/..." required></div><div class="field"><label>표시 이름</label><input name="title" placeholder="제안서, 분석자료, 회의록..."></div><button class="button secondary" type="submit">링크 연결</button></form>
      <div class="workspace-section-title"><div><span class="eyebrow">MY GOOGLE DRIVE</span><h3>내 Drive 탐색</h3></div>${this.connection.connected ? `<button class="button ghost small" type="button" data-google-action="refresh-drive">${icon("refresh")} 새로고침</button>` : ""}</div>
      ${this.connection.connected ? `<div class="drive-toolbar"><select data-google-field="drive">${driveOptions}</select><label class="drive-search">${icon("search")}<input data-google-field="search" value="${esc(this.search)}" placeholder="파일 이름 검색"></label><button class="button secondary small" type="button" data-google-action="search-files">검색</button></div><div class="folder-trail"><button type="button" data-google-action="folder-root">Root</button>${this.folderTrail.map((item, index) => `<span>›</span><button type="button" data-google-action="folder-trail" data-folder-index="${index}">${esc(item.name)}</button>`).join("")}</div>` : `<button class="button google-connect-main" type="button" data-google-action="connect">내 Google 계정 연결</button>`}
      <div class="workspace-file-list">${files}</div>
      ${this.renderPreview()}
    </section>`;
  }


  renderPreviewControls({ zoom = true, label = "" } = {}) {
    const percent = Math.round((this.previewZoom || 1) * 100);
    return `<div class="preview-controlbar"><div class="preview-controlbar-left">${label ? `<span>${esc(label)}</span>` : ""}</div><div class="preview-controlbar-right">${zoom ? `<button class="icon-button" type="button" data-google-action="preview-zoom-out" title="축소">−</button><button class="preview-zoom-label" type="button" data-google-action="preview-zoom-reset" title="100%로 복원">${percent}%</button><button class="icon-button" type="button" data-google-action="preview-zoom-in" title="확대">＋</button>` : ""}<button class="button ghost small" type="button" data-google-action="preview-expand">${this.previewExpanded ? "원래 크기" : "크게 보기"}</button></div></div>`;
  }

  renderDocumentViewerControls(label = "Google 전체 문서 Viewer") {
    const canContent = this.previewNative?.kind === "doc";
    return `<div class="preview-controlbar preview-document-toolbar"><div class="preview-controlbar-left"><span>${esc(label)}</span><small>페이지 전체 · 내부 스크롤 · 원본 화질</small></div><div class="preview-controlbar-right">${canContent ? `<button class="button ghost small ${this.previewMode === "content" ? "active" : ""}" type="button" data-google-action="preview-mode-content">내용 보기</button><button class="button ghost small ${this.previewMode !== "content" ? "active" : ""}" type="button" data-google-action="preview-mode-layout">페이지 보기</button>` : ""}<button class="button ghost small" type="button" data-google-action="preview-expand">${this.previewExpanded ? "원래 크기" : "크게 보기"}</button></div></div>`;
  }

  renderNativePreview() {
    const data = this.previewNative;
    if (!data) return "";
    if (data.kind === "doc") {
      const blocks = (data.blocks || []).map(block => {
        if (block.type === "table") {
          const rows = (block.rows || []).map(row => `<tr>${(row || []).map(cell => `<td>${esc(cell || "")}</td>`).join("")}</tr>`).join("");
          return `<div class="native-doc-table-wrap"><table class="native-doc-table"><tbody>${rows}</tbody></table></div>`;
        }
        const style = String(block.style || "NORMAL_TEXT").toLowerCase().replace(/_/g, "-");
        const bullet = block.bullet ? '<span class="native-doc-bullet">•</span>' : "";
        return `<div class="native-doc-paragraph ${esc(style)}">${bullet}<span>${esc(block.text || "")}</span></div>`;
      }).join("");
      return `${this.renderPreviewControls({ label: `Google Docs · ${data.blocks?.length || 0} blocks` })}<div class="native-preview-note">${icon("doc")} Docs API 연속 읽기 전용 Preview${data.truncated ? " · 매우 긴 문서는 일부만 표시" : " · 전체 내용을 스크롤"}</div><div class="native-doc-preview" style="--preview-zoom:${Number(this.previewZoom || 1).toFixed(2)}"><div class="native-doc-page">${blocks || '<div class="workspace-empty compact">표시할 문서 내용이 없음</div>'}</div></div>`;
    }
    if (data.kind === "sheet") {
      const rows = data.rows || [];
      const width = Math.max(0, ...rows.map(row => row.length));
      const head = rows[0] || [];
      const bodyRows = rows.slice(1);
      const header = width ? `<thead><tr>${Array.from({ length: width }, (_, i) => `<th>${esc(head[i] ?? "")}</th>`).join("")}</tr></thead>` : "";
      const body = bodyRows.map(row => `<tr>${Array.from({ length: width }, (_, i) => `<td>${esc(row[i] ?? "")}</td>`).join("")}</tr>`).join("");
      return `${this.renderPreviewControls({ label: `${data.sheetTitle || "Sheet"} · A1:T60` })}<div class="native-preview-note">${icon("sheet")} Sheets API 읽기 전용 Preview · 가로/세로 스크롤 가능</div><div class="native-sheet-preview" style="--preview-zoom:${Number(this.previewZoom || 1).toFixed(2)}"><div class="native-sheet-zoom"><table>${header}<tbody>${body}</tbody></table></div></div>`;
    }
    if (data.kind === "slide") {
      const slides = data.slides || [];
      const cards = slides.map((slide, index) => {
        const url = this.previewSlideUrls[index] || "";
        return `<article class="native-slide-card" data-slide-index="${index}"><div class="native-slide-number">${index + 1}</div>${url ? `<img src="${esc(url)}" alt="${esc(data.title || "Google Slides")} ${index + 1}">` : `<div class="slide-placeholder"><span></span><strong>${this.previewSlideBatchLoading ? "슬라이드 불러오는 중…" : "미리보기 대기"}</strong></div>`}</article>`;
      }).join("");
      return `${this.renderPreviewControls({ label: `Google Slides · ${slides.length} slides` })}<div class="native-preview-note">${icon("slide")} Slides API 연속 Preview · 모든 슬라이드를 세로로 스크롤</div><div class="native-slide-scroll" style="--preview-zoom:${Number(this.previewZoom || 1).toFixed(2)}">${cards || '<div class="workspace-empty compact">표시할 슬라이드 없음</div>'}</div>`;
    }
    return "";
  }

  renderPreview() {
    if (!this.preview) return "";
    const kind = attachmentKind(this.preview);
    const open = this.preview.webViewLink || this.preview.url || this.preview.metadata?.htmlLink || this.preview.metadata?.meetingUri || "";
    const title = this.preview.name || this.preview.title || "Preview";
    let body = "";
    if (kind === "mail") {
      const meta = this.preview.metadata || {};
      const attachmentNames = Array.isArray(meta.attachments) && meta.attachments.length ? `<div class="mail-preview-attachments"><span>첨부</span><strong>${esc(meta.attachments.join(", "))}</strong></div>` : "";
      const content = meta.bodyText || meta.snippet || "메일 내용을 불러오지 못함";
      body = this.previewLoading
        ? '<div class="preview-loader"><span></span><strong>연결한 사용자의 Gmail에서 메일 본문 불러오는 중…</strong><small>본문은 NAYSOR DB에 저장하지 않음</small></div>'
        : this.previewError
          ? `<div class="workspace-empty preview-error"><div><strong>메일 Preview를 불러올 수 없음</strong><p>${esc(this.previewError)}</p></div></div>`
          : `<div class="mail-preview"><div class="mail-preview-row"><span>From</span><strong>${esc(meta.from || "—")}</strong></div><div class="mail-preview-row"><span>To</span><strong>${esc(meta.to || "—")}</strong></div>${meta.cc ? `<div class="mail-preview-row"><span>Cc</span><strong>${esc(meta.cc)}</strong></div>` : ""}<div class="mail-preview-row"><span>Date</span><strong>${esc(meta.date || "—")}</strong></div>${attachmentNames}<pre class="mail-body-preview">${esc(content)}</pre><p class="mail-preview-policy">메일 본문은 연결한 Google 계정에서 실시간 읽기 전용으로 가져오며 NAYSOR attachment metadata에는 저장하지 않음.</p></div>`;
    } else if (kind === "calendar") {
      const meta = this.preview.metadata || this.preview;
      const meet = meetUrlOf(meta);
      body = `<div class="calendar-preview-card"><div class="calendar-preview-date">${icon("calendar")}<strong>${esc(eventDateText(meta))}</strong></div><div><h4>${esc(meta.summary || title)}</h4>${meta.location ? `<p><b>장소</b> ${esc(meta.location)}</p>` : ""}${meta.description ? `<p>${esc(meta.description)}</p>` : ""}${meet ? `<a class="button meet-button" href="${esc(meet)}" target="_blank" rel="noopener">${icon("meet")} Google Meet 참여</a>` : ""}</div></div>`;
    } else if (kind === "meet") {
      const meta = this.preview.metadata || this.preview;
      body = `<div class="calendar-preview-card"><div class="calendar-preview-date">${icon("meet")}<strong>${esc(meta.startTime ? new Date(meta.startTime).toLocaleString("ko-KR") : "Google Meet")}</strong></div><div><h4>${esc(meta.meetingCode ? `Meet ${meta.meetingCode}` : title)}</h4>${meta.endTime ? `<p>종료 ${esc(new Date(meta.endTime).toLocaleString("ko-KR"))}</p>` : `<p>종료 기록 없음 또는 진행 중</p>`}${meta.meetingUri ? `<a class="button meet-button" href="${esc(meta.meetingUri)}" target="_blank" rel="noopener">${icon("meet")} Meet 열기</a>` : ""}</div></div>`;
    } else if (this.previewLoading) {
      body = '<div class="preview-loader"><span></span><strong>Google 원본에서 읽기 전용 Preview 생성 중…</strong><small>파일은 Supabase Storage에 저장하지 않음</small></div>';
    } else if (this.previewMode === "content" && this.previewNative?.kind === "doc") {
      body = this.renderNativePreview();
    } else if (this.previewOfficeBuffer) {
      const mime = this.previewOfficeMime || this.preview?.mimeType || "";
      const label = mime.includes("wordprocessingml") ? "DOCX · NAYSOR 자체 Viewer"
        : mime.includes("spreadsheetml") ? "XLSX · 원본 Sheet Viewer"
        : mime.includes("presentationml") ? "PPTX · 고화질 전체 슬라이드 Viewer"
        : "Office/Text · NAYSOR 자체 Viewer";
      body = `${this.renderPreviewControls({ label })}<div class="native-preview-note office-self-preview-note">${icon("file")} NAYSOR 서버 중계 + 브라우저 고정밀 렌더링 · Google Drive 권한/Google 로그인 불필요 · 읽기 전용</div><div class="office-self-preview-host" data-office-preview></div>`;
    } else if (this.previewAssetUrl) {
      body = this.previewAssetType.startsWith("image/")
        ? `${this.renderPreviewControls({ label: "이미지 / NAYSOR 서버 중계" })}<div class="preview-image-stage" style="--preview-zoom:${Number(this.previewZoom || 1).toFixed(2)}"><img src="${esc(this.previewAssetUrl)}" alt="${esc(title)}"></div>`
        : `${this.renderPreviewControls({ zoom: false, label: this.previewAssetType === "application/pdf" ? "PDF · NAYSOR 서버 중계 · 연속 페이지 스크롤" : "읽기 전용 Preview" })}<iframe class="secure-preview-frame" src="${esc(this.previewAssetUrl)}#view=FitH" title="${esc(title)} 읽기 전용 미리보기"></iframe>`;
    } else if (this.previewNative) {
      body = this.renderNativePreview();
    } else if (this.previewError) {
      body = `<div class="workspace-empty preview-error"><div><strong>내부 미리보기를 만들 수 없음</strong><p>${esc(this.previewError)}</p>${!this.connection.capabilities?.drivePreview && kind !== "file" ? '<button class="button small" type="button" data-google-action="connect">Google Drive 읽기 권한 확장</button>' : ""}</div></div>`;
    } else {
      body = '<div class="workspace-empty">이 자료는 Google 원본에서 열어 확인함.</div>';
    }
    return `<section class="workspace-preview secure-preview ${this.previewExpanded ? "is-expanded" : ""}"><header><div><span class="eyebrow">READ-ONLY PREVIEW</span><h3>${esc(title)}</h3>${this.renderFileDetailSummary()}</div><div>${open ? `<a class="button secondary small" href="${esc(open)}" target="_blank" rel="noopener">${icon("external")} Google에서 열기</a>` : ""}<button class="icon-button" type="button" data-google-action="close-preview">×</button></div></header>${body}</section>`;
  }

  renderFileDetailSummary() {
    const meta = this.previewDetails?.workspaceMeta;
    if (!meta) return "";
    if (meta.kind === "sheet") return `<small class="preview-meta-line">Sheets API · ${meta.sheets?.length || 0}개 시트${meta.sheets?.length ? ` · ${esc(meta.sheets.slice(0, 3).map(item => item.title).join(", "))}` : ""}</small>`;
    if (meta.kind === "slide") return `<small class="preview-meta-line">Slides API · ${meta.slideCount || 0} slides</small>`;
    if (meta.kind === "doc") return `<small class="preview-meta-line">Docs API · read-only metadata</small>`;
    const mime = this.previewDetails?.resolvedMimeType || this.preview?.mimeType || "";
    if (mime) return `<small class="preview-meta-line">Drive MIME · ${esc(mime)}${this.previewDetails?.isShortcut ? " · Shortcut resolved" : ""}</small>`;
    return "";
  }

  calendarStorageKey() {
    return `naysor-calendar-visible-v29:${this.platform.profile?.id || this.connection.profileId || "anon"}`;
  }

  ensureCalendarSelection() {
    if (this.calendarSelectedIds.size) return;
    try {
      const saved = JSON.parse(localStorage.getItem(this.calendarStorageKey()) || "[]");
      if (Array.isArray(saved)) saved.forEach(id => {
        if (this.calendars.some(calendar => calendar.id === id)) this.calendarSelectedIds.add(id);
      });
    } catch { /* ignore */ }
    if (!this.calendarSelectedIds.size) {
      const selectedByGoogle = this.calendars.filter(calendar => calendar.selected || calendar.primary);
      (selectedByGoogle.length ? selectedByGoogle : this.calendars.slice(0, 1)).forEach(calendar => {
        if (calendar?.id) this.calendarSelectedIds.add(calendar.id);
      });
    }
  }

  persistCalendarSelection() {
    localStorage.setItem(this.calendarStorageKey(), JSON.stringify([...this.calendarSelectedIds]));
  }

  calendarGridBounds() {
    const first = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 42);
    return { start, end };
  }

  eventKey(event = {}) {
    return `${event.calendarId || "primary"}::${event.id || ""}`;
  }

  eventByKey(key) {
    return this.calendarDisplayEvents().find(item => this.eventKey(item) === key);
  }

  eventDayKey(event = {}) {
    const raw = event?.start?.dateTime || event?.start?.date || "";
    if (!raw) return "";
    if (event?.start?.date) return String(raw).slice(0, 10);
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw).slice(0, 10);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  eventCoversDay(event = {}, dayKey = "") {
    if (!dayKey) return false;
    const startKey = this.eventDayKey(event);
    if (!startKey) return false;
    const rawEnd = event?.end?.dateTime || event?.end?.date || "";
    if (!rawEnd) return startKey === dayKey;
    if (event?.end?.date) {
      // Google all-day event end dates are exclusive.
      const endExclusive = String(rawEnd).slice(0, 10);
      return dayKey >= startKey && dayKey < endExclusive;
    }
    const end = new Date(rawEnd);
    if (Number.isNaN(end.getTime())) return startKey === dayKey;
    // A timed event ending exactly at 00:00 belongs to the previous day only.
    if (end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0) end.setMilliseconds(end.getMilliseconds() - 1);
    const endKey = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
    return dayKey >= startKey && dayKey <= endKey;
  }

  calendarMonthTitle() {
    return new Intl.DateTimeFormat(googleLocale(), { year: "numeric", month: "long" }).format(this.calendarMonth);
  }

  renderCalendarMonth() {
    const { start } = this.calendarGridBounds();
    const todayKey = new Date().toLocaleDateString("sv-SE");
    const weekdayFormatter = new Intl.DateTimeFormat(googleLocale(), { weekday: "short" });
    const headers = Array.from({ length: 7 }, (_, index) => weekdayFormatter.format(new Date(2026, 7, 2 + index))).map(day => `<div class="naysor-cal-weekday">${esc(day)}</div>`).join("");
    const displayEvents = this.calendarDisplayEvents();
    const cells = [];
    for (let index = 0; index < 42; index += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const key = date.toLocaleDateString("sv-SE");
      const events = displayEvents.filter(item => this.eventCoversDay(item, key))
        .filter(item => !this.calendarQuery || `${item.summary || ""} ${item.location || ""} ${item.calendarSummary || ""}`.toLowerCase().includes(this.calendarQuery.toLowerCase()))
        .sort((a, b) => Number(Boolean(b._naysorTagged)) - Number(Boolean(a._naysorTagged)) || String(a.start?.dateTime || a.start?.date || "").localeCompare(String(b.start?.dateTime || b.start?.date || "")));
      const shown = events.slice(0, 7).map(item => {
        const color = item.calendarColor || "#2383d8";
        const time = item.start?.dateTime ? new Date(item.start.dateTime).toLocaleTimeString(googleLocale(),{hour:"2-digit",minute:"2-digit"}) : "";
        const eventKey = this.eventKey(item);
        return `<button class="naysor-cal-event ${item.hangoutLink ? "has-meet" : ""} ${item._naysorTagged ? "is-tagged" : ""} ${eventKey === this.selectedCalendarEventKey ? "is-selected" : ""}" type="button" data-google-action="select-calendar-event" data-event-key="${esc(eventKey)}" style="--event-color:${esc(color)}" title="${esc(item.summary || "")}${item._naysorTagged ? " · 업무 Context에 태그됨" : ""}"><span class="naysor-cal-dot"></span>${time ? `<small>${esc(time)}</small>` : ""}<strong>${esc(item.summary || "(제목 없음)")}</strong>${item._naysorTagged ? `<span class="naysor-cal-linked" aria-label="업무 Context에 태그됨">${icon("link")}</span>` : ""}</button>`;
      }).join("");
      const muted = date.getMonth() !== this.calendarMonth.getMonth();
      cells.push(`<div class="naysor-cal-day ${muted ? "is-muted" : ""} ${key === todayKey ? "is-today" : ""}">
        <div class="naysor-cal-day-number">${date.getDate()}</div>
        <div class="naysor-cal-events">${shown}${events.length > 7 ? `<span class="naysor-cal-more">+${events.length - 7}개 더보기</span>` : ""}</div>
      </div>`);
    }
    return `<div class="naysor-calendar-grid"><div class="naysor-cal-weekdays">${headers}</div><div class="naysor-cal-days">${cells.join("")}</div></div>`;
  }

  renderCalendarInspector() {
    const event = this.eventByKey(this.selectedCalendarEventKey);
    if (!event) return `<div class="calendar-selection-empty">캘린더에서 일정을 클릭하면 상세내용과 Activity/Phase 연결 버튼이 표시됨.</div>`;
    const meet = meetUrlOf(event);
    const currentEntity = this.entity?.id ? `${this.entity.type} · ${this.entity.id}` : "연결 대상 미선택";
    const tagged = Boolean(event._naysorTagged);
    return `<article class="calendar-event-inspector">
      <div class="calendar-event-inspector-head"><div><span class="eyebrow">SELECTED EVENT</span><h3>${esc(event.summary || "(제목 없음)")}</h3><p>${esc(eventDateText(event))}${event.calendarSummary ? ` · ${esc(event.calendarSummary)}` : ""}</p>${tagged ? '<span class="calendar-linked-badge">업무 Context에 태그됨</span>' : ""}</div><span class="calendar-color-chip" style="--event-color:${esc(event.calendarColor || "#2383d8")}"></span></div>
      ${event.description ? `<p class="calendar-event-description">${esc(event.description)}</p>` : ""}
      ${event.location ? `<p><b>장소</b> ${esc(event.location)}</p>` : ""}
      <div class="calendar-event-inspector-actions">
        ${event.htmlLink ? `<a class="button secondary small" href="${esc(event.htmlLink)}" target="_blank" rel="noopener">Google Calendar에서 열기</a>` : ""}
        ${meet ? `<a class="button secondary small" href="${esc(meet)}" target="_blank" rel="noopener">${icon("meet")} Meet 참여</a>` : ""}
        ${tagged ? '<span class="calendar-linked-action">현재 업무에 태그됨</span>' : `<button class="button small" type="button" data-google-action="attach-calendar-event" data-event-key="${esc(this.eventKey(event))}">현재 ${esc(currentEntity)}에 일정 태그</button>`}
      </div>
      <p class="calendar-context-policy">이 일정은 CPM Activity를 생성하거나 날짜를 변경하지 않음. 선택한 업무 항목의 Context 자료로만 공유됨.</p>
    </article>`;
  }

  renderCalendar() {
    this.ensureCalendarSelection();
    const taggedCount = this.currentTaggedCalendarAttachments().length;
    const taggedLabel = this.entity.type === "activity" ? "현재 Activity에 태그된 일정" : "현재 업무 태그 일정";
    const calendarList = this.calendars.map(calendar => {
      const checked = this.calendarSelectedIds.has(calendar.id);
      return `<label class="naysor-calendar-source"><input type="checkbox" data-google-field="calendar-visible" value="${esc(calendar.id)}" ${checked ? "checked" : ""}><span class="calendar-source-color" style="--calendar-color:${esc(calendar.backgroundColor || "#2383d8")}"></span><span><strong>${esc(calendar.summary || calendar.id)}</strong><small>${calendar.primary ? "내 기본 캘린더" : esc(calendar.accessRole || "shared")}</small></span></label>`;
    }).join("");
    return `<section class="workspace-section calendar-context-section">
      ${this.renderEntityPicker()}
      ${this.renderPersonalGoogleBanner("내 Calendar · Meet", "내 계정에 표시되는 Google Calendar 목록을 그대로 불러옴. 공유받은 팀원 캘린더도 CalendarList에 있으면 선택해 함께 볼 수 있음.")}
      ${!this.connection.capabilities?.calendar ? `<div class="workspace-note scope-warning"><strong>Google Calendar 읽기 권한 필요</strong><p>개인 Calendar를 추가로 불러오려면 내 Google 계정을 연결하세요. 이미 업무에 태그된 일정은 아래 월간 화면에서 계속 확인할 수 있습니다.</p><button class="button small" type="button" data-google-action="connect">내 Google 연결</button></div>` : ""}
      <div class="naysor-calendar-shell">
        <aside class="naysor-calendar-sidebar">
          <div class="calendar-side-title"><strong>내 캘린더</strong><button class="icon-button small" type="button" data-google-action="refresh-calendar" title="새로고침">${icon("refresh")}</button></div>
          <div class="naysor-calendar-linked-source"><span class="calendar-source-color"></span><span><strong>${taggedLabel}</strong><small>월간 화면에 항상 표시</small></span><b>${taggedCount}</b></div>
          <div class="naysor-calendar-source-list">${calendarList || '<div class="workspace-empty compact">표시 가능한 Calendar 없음</div>'}</div>
          <div class="calendar-side-note">체크한 캘린더만 월간 화면에 겹쳐 표시됨. 팀원이 공유한 캘린더도 내 Google Calendar 목록에 추가돼 있으면 여기 나타남.</div>
        </aside>
        <div class="naysor-calendar-main">
          <div class="naysor-calendar-toolbar">
            <button class="button secondary small" type="button" data-google-action="calendar-today">오늘</button>
            <button class="icon-button" type="button" data-google-action="calendar-prev" aria-label="이전 달">‹</button>
            <button class="icon-button" type="button" data-google-action="calendar-next" aria-label="다음 달">›</button>
            <h3>${esc(this.calendarMonthTitle())}</h3>
            <form class="naysor-calendar-search" data-google-form="calendar-search"><label>${icon("search")}<input name="query" value="${esc(this.calendarQuery)}" placeholder="일정 검색"></label><button class="button secondary small" type="submit">검색</button></form>
          </div>
          ${this.renderCalendarMonth()}
          ${this.renderCalendarInspector()}
        </div>
      </div>
      <div class="workspace-note calendar-context-only-note"><strong>Calendar는 CPM·Gantt의 원본 일정이 아님</strong><p>CPM Activity는 책임자·Start/Deadline·진도율·선행관계·Cost/Baseline을 통제하는 로드맵임. Google Calendar는 회의·출장·리마인더 같은 실제 일정을 <b>기존 Activity/Phase에 태그</b>하는 Context로만 사용하며, Calendar 일정이 Activity를 새로 만들거나 CPM 날짜를 덮어쓰지 않음.</p></div>
      ${this.renderPreview()}
    </section>`;
  }

  renderMail() {
    const linked = this.currentAttachments().filter(item => attachmentKind(item) === "mail");
    const results = this.emails.map(item => `<article class="mail-result-card"><button class="mail-result-main" type="button" data-google-action="preview-gmail" data-message-id="${esc(item.id)}"><span class="workspace-file-icon mail">${icon("mail")}</span><span><strong>${esc(item.subject)}</strong><small>${esc(item.from || "")}${item.date ? ` · ${esc(item.date)}` : ""}</small><em>${esc(item.snippet || "")}</em></span></button><a class="icon-button" href="${esc(item.url)}" target="_blank" rel="noopener">↗</a><button class="button small" type="button" data-google-action="attach-gmail" data-message-id="${esc(item.id)}">연결</button></article>`).join("") || `<div class="workspace-empty compact">${this.connection.capabilities?.gmail ? "최근 받은 메일이 없거나 검색 결과가 없음" : "내 Google 계정을 연결하면 받은 메일을 조회할 수 있음"}</div>`;
    return `<section class="workspace-section">
      ${this.renderEntityPicker()}
      ${this.renderPersonalGoogleBanner("내 Gmail", "내 받은편지함을 검색함. 메일은 자동 공유되지 않고, 내가 ‘연결’한 메일만 Project Context에서 팀이 읽기 전용 Preview할 수 있음.")}
      <div class="workspace-section-title"><div><span class="eyebrow">SHARED LINKED MAIL</span><h3>이 업무에 연결된 메일 ${linked.length}개</h3></div></div>
      <div class="workspace-linked-section">${linked.map(item => this.renderResourceCard(item)).join("") || '<div class="workspace-empty compact">이 업무에 공유된 Gmail 없음</div>'}</div>
      <div class="workspace-section-title"><div><span class="eyebrow">MY GMAIL INBOX</span><h3>내 받은 메일 찾아 연결</h3></div>${this.connection.capabilities?.gmail ? `<button class="button ghost small" type="button" data-google-action="refresh-gmail">${icon("refresh")} 새로고침</button>` : ""}</div>
      ${!this.connection.capabilities?.gmail ? `<div class="workspace-note scope-warning"><strong>내 Gmail 읽기 권한 필요</strong><p>본인이 사용할 Google 계정을 선택해 연결하면 받은 메일을 검색할 수 있음. 메일 전송·수정은 하지 않음.</p><button class="button small" type="button" data-google-action="connect">내 Google 연결</button></div>` : `<form class="gmail-search-form" data-google-form="gmail-search"><label class="gmail-query-field">${icon("search")}<input name="query" value="${esc(this.gmailQuery)}" placeholder="예: in:inbox newer_than:90d PT.SATU"></label><button class="button gmail-search-button" type="submit">Gmail 검색</button></form>`}
      <div class="mail-result-list">${results}</div>
      ${this.renderPreview()}
    </section>`;
  }

  renderMeet() {
    const records = this.meetRecords.map(record => `<article class="meet-record-card"><div class="workspace-file-icon meet">${icon("meet")}</div><div><strong>${esc(record.meetingCode || "Google Meet")}</strong><small>${esc(record.startTime ? new Date(record.startTime).toLocaleString("ko-KR") : "")}${record.endTime ? ` → ${esc(new Date(record.endTime).toLocaleString("ko-KR"))}` : " · 진행 중"}</small></div>${record.meetingUri ? `<a class="resource-quick-action meet" href="${esc(record.meetingUri)}" target="_blank" rel="noopener">${icon("meet")} 열기</a>` : ""}<button class="button small" type="button" data-google-action="attach-meet-record" data-meet-name="${esc(record.name)}">연결</button></article>`).join("") || '<div class="workspace-empty compact">최근 Meet 기록을 불러오면 여기에 표시됨</div>';
    return `<section class="workspace-section">
      ${this.renderEntityPicker()}
      ${this.renderPersonalGoogleBanner("내 Meet 기록", "현재 로그인 사용자의 Google Meet 회의 기록만 조회함. 필요한 회의만 Project Context에 연결해 공유함.")}
      <div class="workspace-section-title"><div><span class="eyebrow">MY GOOGLE MEET</span><h3>내 최근 회의 기록</h3></div>${this.connection.capabilities?.meet ? `<button class="button ghost small" type="button" data-google-action="refresh-meet">${icon("refresh")} 새로고침</button>` : ""}</div>
      <div class="workspace-note"><strong>일정 관리는 Calendar가 기준</strong><p>예정된 Meet는 Calendar 탭에서 관리하고, 이 화면은 Meet API로 최근 회의 공간·시작/종료 기록을 빠르게 확인하는 보조 화면임.</p></div>
      ${!this.connection.capabilities?.meet ? `<div class="workspace-note scope-warning"><strong>Meet metadata 권한 필요</strong><p>회의실 메타데이터만 읽으며 녹화·스크립트는 자동 다운로드하지 않음.</p><button class="button small" type="button" data-google-action="connect">권한 확장</button></div>` : `<div class="meet-record-list">${records}</div>`}
    </section>`;
  }

  renderSettings() {
    const missing = this.connection.missingScopes || [];
    const capability = (ok, iconName, title, text) => `<div class="${ok ? "scope-ok" : "scope-missing"}">${icon(iconName)}<span><strong>${title}${ok ? " · 연결됨" : " · 권한 필요"}</strong><small>${text}</small></span></div>`;
    return `<section class="workspace-section">
      <div class="connection-card ${this.connection.connected ? "connected" : ""}"><div class="workspace-brand-icon large">${icon("drive")}</div><div><span class="eyebrow">MY GOOGLE CONNECTION</span><h3>${this.connection.connected ? "내 Google 연결됨" : "내 Google 연결 필요"}</h3><p>${this.connection.connected ? `PM 로그인 · ${esc(this.connection.pmEmail || "—")}<br>Google · ${esc(this.connection.email || "—")}` : esc(this.connection.error || this.connection.lastError || `PM 로그인(${this.connection.pmEmail || "현재 사용자"})에 연결할 본인의 Google 계정을 선택함`)}</p></div></div>
      <div class="permission-list">
        ${capability(Boolean(this.connection.capabilities?.drivePreview), "drive", "Drive read-only", "Docs·Sheets·Slides를 클릭할 때만 PDF/이미지 Preview로 스트리밍")}
        ${capability(Boolean(this.connection.capabilities?.calendar), "calendar", "Calendar read-only", "내 Calendar·공유 Calendar 조회와 Project Context 일정 태그")}
        ${capability(Boolean(this.connection.capabilities?.gmail), "mail", "Gmail read-only", "관련 메일 검색·태그. 메일 전송/수정 기능 없음")}
        ${capability(Boolean(this.connection.capabilities?.meet), "meet", "Meet metadata read-only", "최근 회의 공간과 시작·종료 기록 확인")}
      </div>
      ${missing.length ? `<div class="workspace-note scope-warning"><strong>권한 확장 필요</strong><p>${esc(missing.join("\n"))}</p><button class="button" type="button" data-google-action="connect">Google 권한 다시 승인</button></div>` : ""}
      <div class="workspace-note personal-connection-note"><strong>개인 Google 연결 + 공유 Context</strong><p>PM 로그인 주소와 별개로 본인이 소유한 검증된 Google 계정을 선택할 수 있음. 내 Drive·Gmail·Calendar·Meet는 나에게만 탐색되고, Activity/Phase에 직접 ‘연결’한 항목만 팀 Context가 됨. Calendar는 CPM Activity를 생성하거나 날짜를 변경하지 않음.</p></div>
      <div class="form-actions">${this.connection.connected ? `<button class="button secondary" type="button" data-google-action="connect">권한 다시 승인</button><button class="button danger" type="button" data-google-action="disconnect">내 Google 연결 해제</button>` : `<button class="button" type="button" data-google-action="connect">내 Google 계정 연결</button>`}</div>
    </section>`;
  }

  fileById(id) { return this.files.find(file => file.id === id); }
  emailById(id) { return this.emails.find(item => item.id === id); }
  eventById(id) { return this.calendarEvents.find(item => item.id === id); }
  meetByName(name) { return this.meetRecords.find(item => item.name === name); }
  attachmentById(id) { return (this.state.attachments || []).find(item => String(item.id) === String(id)); }
  calendarLinkById(id) { return (this.state.googleCalendarEventLinks || []).find(item => String(item.id) === String(id)); }

  async onClick(event) {
    const jump = event.target.closest("[data-google-tab-jump]")?.dataset.googleTabJump;
    if (jump) return this.switchTab(jump);
    const tab = event.target.closest("[data-google-tab]")?.dataset.googleTab;
    if (tab) return this.switchTab(tab);
    const button = event.target.closest("[data-google-action]");
    if (!button) return;
    const action = button.dataset.googleAction;
    if (action === "close") return this.close();
    if (action === "connect") return this.connect();
    if (action === "disconnect") return this.disconnect();
    if (action === "refresh-drive") return this.loadDrive(true);
    if (action === "search-files") return this.loadFiles();
    if (action === "folder-root") { this.folderId = ""; this.folderTrail = []; return this.loadFiles(); }
    if (action === "folder-trail") { const index = Number(button.dataset.folderIndex); const item = this.folderTrail[index]; this.folderTrail = this.folderTrail.slice(0, index + 1); this.folderId = item?.id || ""; return this.loadFiles(); }
    if (action === "open-folder") { const file = this.fileById(button.dataset.fileId); if (file) { this.folderId = file.id; this.folderTrail.push({ id: file.id, name: file.name }); await this.loadFiles(); } return; }
    if (action === "preview-file") return this.openPreview(normalizeGoogleFile(this.fileById(button.dataset.fileId)));
    if (action === "close-preview") { this.preview = null; this.releasePreviewAsset(); return this.render(); }
    if (action === "slide-prev") return this.loadSlideThumbnail(this.previewSlideIndex - 1);
    if (action === "slide-next") return this.loadSlideThumbnail(this.previewSlideIndex + 1);
    if (action === "preview-expand") { this.previewExpanded = !this.previewExpanded; this.root?.classList.toggle("preview-expanded", this.previewExpanded); return this.render(); }
    if (action === "preview-mode-layout") { this.previewMode = "layout"; return this.render(); }
    if (action === "preview-mode-content") { this.previewMode = "content"; return this.render(); }
    if (action === "preview-zoom-in") { this.previewZoom = Math.min(2, Math.round(((this.previewZoom || 1) + 0.1) * 10) / 10); return this.render(); }
    if (action === "preview-zoom-out") { this.previewZoom = Math.max(0.6, Math.round(((this.previewZoom || 1) - 0.1) * 10) / 10); return this.render(); }
    if (action === "preview-zoom-reset") { this.previewZoom = 1; return this.render(); }
    if (action === "attach-file") return this.attachFile(this.fileById(button.dataset.fileId));
    if (action === "recommend-context") return this.recommendContext();
    if (action === "preview-recommendation") return this.openPreview(this.recommendationMap.get(button.dataset.recommendationKey));
    if (action === "attach-recommendation") return this.attachFile(this.recommendationMap.get(button.dataset.recommendationKey));
    if (action === "preview-attachment") return this.openPreview(this.attachmentById(button.dataset.attachmentId));
    if (action === "preview-sync-event") { const link = this.calendarLinkById(button.dataset.calendarLinkId); if (link) return this.openPreview({ id: link.id, title: link.snapshot?.summary || "Calendar Event", provider: "google_calendar", mimeType: "application/vnd.google-apps.calendar-event", url: link.url, webViewLink: link.url, metadata: link.snapshot || {} }); }
    if (action === "delete-attachment") return this.deleteAttachment(button.dataset.attachmentId);
    if (action === "select-calendar-event") { this.selectedCalendarEventKey = button.dataset.eventKey || ""; return this.render(); }
    if (action === "preview-calendar-event") return this.openPreview(this.calendarAttachment(this.eventByKey(button.dataset.eventKey || "") || this.eventById(button.dataset.eventId)));
    if (action === "attach-calendar-event") return this.attachFile(this.calendarAttachment(this.eventByKey(button.dataset.eventKey || "") || this.eventById(button.dataset.eventId)));
    if (action === "calendar-prev") { this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() - 1, 1); this.selectedCalendarEventKey = ""; return this.loadCalendarEvents(); }
    if (action === "calendar-next") { this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 1); this.selectedCalendarEventKey = ""; return this.loadCalendarEvents(); }
    if (action === "calendar-today") { const now = new Date(); this.calendarMonth = new Date(now.getFullYear(), now.getMonth(), 1); this.selectedCalendarEventKey = ""; return this.loadCalendarEvents(); }
    if (action === "refresh-calendar") return this.loadCalendars(true);
    if (action === "preview-gmail") return this.openPreview(this.gmailAttachment(this.emailById(button.dataset.messageId)));
    if (action === "attach-gmail") return this.attachFile(this.gmailAttachment(this.emailById(button.dataset.messageId)));
    if (action === "refresh-gmail") return this.loadGmail();
    if (action === "refresh-meet") return this.loadMeetRecords();
    if (action === "attach-meet-record") return this.attachFile(this.meetAttachment(this.meetByName(button.dataset.meetName)));
  }

  async switchTab(tab) {
    this.activeTab = tab;
    this.preview = null;
    this.releasePreviewAsset();
    if (tab === "calendar" && this.connection.connected) {
      if (!this.calendars.length) await this.loadCalendars(false);
      if (this.calendars.length && !this.calendarEvents.length) await this.loadCalendarEvents(false);
    }
    if (tab === "drive" && this.connection.connected && !this.drives.length && !this.files.length) await this.loadDrive();
    if (tab === "mail" && this.connection.capabilities?.gmail && !this.emails.length) await this.loadGmail(false);
    if (tab === "meet" && this.connection.capabilities?.meet && !this.meetRecords.length) await this.loadMeetRecords(false);
    this.render();
  }

  async onChange(event) {
    const field = event.target.dataset.googleField;
    if (field === "entity") {
      const [type, ...parts] = event.target.value.split(":");
      this.entity = { type, id: parts.join(":") };
      this.recommendations = [];
      this.recommendationMap.clear();
      this.recommendationsLoaded = false;
      this.preview = null;
      this.releasePreviewAsset();
      this.render();
      return;
    }
    if (field === "drive") { this.driveId = event.target.value; this.folderId = ""; this.folderTrail = []; await this.loadFiles(); }
    if (field === "search") this.search = event.target.value;
    if (field === "calendar-visible") {
      if (event.target.checked) this.calendarSelectedIds.add(event.target.value);
      else this.calendarSelectedIds.delete(event.target.value);
      if (!this.calendarSelectedIds.size) {
        const primary = this.calendars.find(calendar => calendar.primary) || this.calendars[0];
        if (primary?.id) this.calendarSelectedIds.add(primary.id);
      }
      this.persistCalendarSelection();
      await this.loadCalendarEvents();
    }
  }

  async onSubmit(event) {
    const formName = event.target.dataset.googleForm;
    if (!formName) return;
    event.preventDefault();
    if (formName === "manual-link") {
      const data = Object.fromEntries(new FormData(event.target).entries());
      const file = inferManualLink(data.url, data.title);
      await this.attachFile(file);
      event.target.reset();
    }
    if (formName === "calendar-search") {
      const data = Object.fromEntries(new FormData(event.target).entries());
      this.calendarQuery = String(data.query || "");
      this.render();
    }
    if (formName === "gmail-search") {
      const data = Object.fromEntries(new FormData(event.target).entries());
      this.gmailQuery = String(data.query || "");
      await this.loadGmail();
    }
  }

  async connect() {
    if (!this.service) return this.toast("Supabase 운영 모드에서 Google OAuth 연결 가능");
    try {
      this.setLoading(true);
      const result = await this.service.connect(window.location.href);
      if (!result.authorizationUrl) throw new Error("Google 인증 주소가 반환되지 않음");
      window.location.assign(result.authorizationUrl);
    } catch (error) { this.toast(error.message); this.setLoading(false); }
  }

  async disconnect() {
    if (!this.service || !confirm("내 Google 연결을 해제할까요? 내가 Project Context에 연결한 Drive/Gmail/Calendar/Meet 자료의 실시간 Preview가 더 이상 작동하지 않을 수 있음.")) return;
    try { this.setLoading(true); await this.service.disconnect(); this.drives = []; this.files = []; this.calendars = []; this.calendarEvents = []; this.emails = []; this.meetRecords = []; await this.refreshStatus(); this.toast("Google 연결을 해제함"); }
    catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadDrive(force = false) {
    if (!this.service || !this.connection.connected) return;
    try {
      this.setLoading(true);
      if (force || !this.drives.length) this.drives = (await this.service.sharedDrives()).drives || [];
      await this.loadFiles();
    } catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadFiles() {
    if (!this.service || !this.connection.connected) return;
    try {
      this.setLoading(true);
      const result = await this.service.files({ driveId: this.driveId, folderId: this.folderId, search: this.search });
      this.files = (result.files || []).map(normalizeGoogleFile);
      this.render();
    } catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadCalendars(renderAfter = true) {
    if (!this.service || !this.connection.connected) return;
    try {
      this.setLoading(true);
      this.calendars = (await this.service.calendars()).calendars || [];
      this.calendarSelectedIds = new Set();
      this.ensureCalendarSelection();
      if (this.connection.capabilities?.calendar) await this.loadCalendarEvents(false);
      if (renderAfter) this.render();
    }
    catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadCalendarEvents(renderAfter = true) {
    if (!this.service || !this.connection.connected || !this.connection.capabilities?.calendar) return;
    if (!this.calendars.length) await this.loadCalendars(false);
    this.ensureCalendarSelection();
    const { start, end } = this.calendarGridBounds();
    const calendarIds = [...this.calendarSelectedIds];
    try {
      this.setLoading(true);
      const result = await this.service.calendarEventsBatch({
        calendarIds,
        timeMin: start.toISOString(),
        timeMax: end.toISOString(),
        maxResults: 250
      });
      this.calendarEvents = result.events || [];
      if (this.selectedCalendarEventKey && !this.eventByKey(this.selectedCalendarEventKey)) this.selectedCalendarEventKey = "";
      if (renderAfter) this.render();
    } catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadGmail(renderAfter = true) {
    if (!this.service || !this.connection.capabilities?.gmail) return;
    if (!String(this.gmailQuery || "").trim()) this.gmailQuery = "in:inbox newer_than:90d";
    try {
      this.setLoading(true);
      this.emails = (await this.service.gmailSearch(this.gmailQuery, 30)).messages || [];
      if (renderAfter) this.render();
    }
    catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async loadMeetRecords(renderAfter = true) {
    if (!this.service || !this.connection.capabilities?.meet) return;
    try { this.setLoading(true); this.meetRecords = (await this.service.meetRecords(20)).records || []; if (renderAfter) this.render(); }
    catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  calendarAttachment(event) {
    if (!event) return null;
    return {
      id: this.eventKey(event),
      externalId: this.eventKey(event),
      name: event.summary || "Google Calendar Event",
      title: event.summary || "Google Calendar Event",
      provider: "google_calendar",
      sourceProfileId: this.platform.profile?.id || this.connection.profileId || "",
      sourceGoogleEmail: this.connection.email || "",
      mimeType: "application/vnd.google-apps.calendar-event",
      webViewLink: event.htmlLink || "",
      url: event.htmlLink || "",
      metadata: event,
      modifiedTime: event.updated || null
    };
  }

  gmailAttachment(message) {
    if (!message) return null;
    return {
      id: message.id,
      externalId: message.id,
      name: message.subject || "Gmail message",
      title: message.subject || "Gmail message",
      provider: "google_gmail",
      sourceProfileId: this.platform.profile?.id || this.connection.profileId || "",
      sourceGoogleEmail: this.connection.email || "",
      mimeType: "message/rfc822",
      webViewLink: message.url || googleGmailUrl(message),
      url: message.url || googleGmailUrl(message),
      metadata: message,
      modifiedTime: message.internalDate ? new Date(Number(message.internalDate)).toISOString() : null
    };
  }

  meetAttachment(record) {
    if (!record) return null;
    return {
      id: record.name || record.meetingCode,
      externalId: record.name || record.meetingCode,
      name: record.meetingCode ? `Google Meet ${record.meetingCode}` : "Google Meet",
      title: record.meetingCode ? `Google Meet ${record.meetingCode}` : "Google Meet",
      provider: "google_meet",
      sourceProfileId: this.platform.profile?.id || this.connection.profileId || "",
      sourceGoogleEmail: this.connection.email || "",
      mimeType: "application/vnd.google-apps.meet",
      webViewLink: record.meetingUri || "",
      url: record.meetingUri || "",
      metadata: record,
      modifiedTime: record.startTime || null
    };
  }

  async attachFile(file) {
    if (!file) return;
    if (!SUPPORTED_ENTITIES.has(this.entity.type)) return this.toast("지원하지 않는 연결 대상임");
    const normalized = normalizeGoogleFile(file);
    const resource = String(normalized.provider || "").startsWith("google_")
      ? { ...normalized, sourceProfileId: normalized.sourceProfileId || this.platform.profile?.id || this.connection.profileId || "", sourceGoogleEmail: normalized.sourceGoogleEmail || this.connection.email || "" }
      : normalized;
    if (resource.provider === "google_gmail" && !confirm("이 메일을 Project Context에 연결하면, 현재 업무 항목을 볼 수 있는 PM 사용자들이 메일 본문을 읽기 전용 Preview할 수 있음. 연결할까요?")) return;
    const storedResource = resource.provider === "google_gmail"
      ? { ...resource, metadata: { ...(resource.metadata || {}), snippet: undefined } }
      : resource;
    try {
      this.setLoading(true);
      const created = await this.platform.createExternalAttachment(this.entity.type, this.entity.id, storedResource);
      await window.NAYSOR_APP?.reload?.();
      await this.openPreview(resource, false);
      this.render();
      this.decorateEntities();
      this.toast("업무 Context에 자료를 연결함 · 보존 Snapshot 생성 중");
      if (this.service && created?.id && String(resource.provider || "").startsWith("google_")) {
        this.service.captureSnapshot(created.id).then(async result => {
          await window.NAYSOR_APP?.reload?.();
          this.toast(result?.snapshotStatus === "ready" ? "공유 Snapshot 보존 완료" : "Context는 연결됐지만 Snapshot 보존 상태를 확인하세요.");
        }).catch(error => this.toast(`Context 연결 완료 · Snapshot 오류: ${error.message || error}`));
      }
    } catch (error) { this.toast(error.message || String(error)); }
    finally { this.setLoading(false); }
  }

  async deleteAttachment(id) {
    if (!confirm("이 연결만 제거할까요? Google 원본은 삭제되지 않음.")) return;
    try {
      this.setLoading(true);
      await this.platform.deleteExternalAttachment(id);
      await window.NAYSOR_APP?.reload?.();
      if (String(this.preview?.id) === String(id)) { this.preview = null; this.releasePreviewAsset(); }
      this.render(); this.decorateEntities(); this.toast("자료 연결을 제거함");
    } catch (error) { this.toast(error.message); }
    finally { this.setLoading(false); }
  }

  async openPreview(item, rerender = true) {
    if (!item) return;
    this.releasePreviewAsset();
    this.preview = normalizeGoogleFile(item);
    this.previewError = "";
    this.previewDetails = null;
    const linkedAttachment = (this.state.attachments || []).find(row => String(row.id) === String(item.id));
    const attachmentId = linkedAttachment?.id || "";
    const initialKind = attachmentKind(this.preview);

    // Shared linked Gmail is read with the Google token of the person who originally linked it.
    // Personal Gmail browsing uses the current user's own connection.
    if (initialKind === "mail") {
      if (!this.service || !this.preview.externalId) { if (rerender) this.render(); return; }
      if (!attachmentId && !this.connection.capabilities?.gmail) {
        this.previewError = "내 Gmail을 먼저 연결해야 개인 메일을 볼 수 있음.";
        if (rerender) this.render();
        return;
      }
      this.previewLoading = true;
      this.render();
      try {
        const cacheKey=`mail:${attachmentId||this.preview.externalId}`;
        const message=this.previewCache.get(cacheKey)||await this.service.gmailMessage(this.preview.externalId, attachmentId);
        this.previewCache.set(cacheKey,message);
        this.preview.metadata = { ...(this.preview.metadata || {}), ...message };
        if (message?.sourceGoogleEmail) this.preview.sourceGoogleEmail = message.sourceGoogleEmail;
      } catch (error) {
        this.previewError = error.message || String(error);
      } finally {
        this.previewLoading = false;
        this.render();
      }
      return;
    }

    // Calendar/Meet are linked as safe snapshots so every allowed PM viewer can inspect them
    // without opening the source user's personal Calendar/Meet account.
    if (["calendar", "meet"].includes(initialKind) || this.preview.provider !== "google_drive" || !this.preview.externalId) {
      if (rerender) this.render();
      return;
    }

    // A personal Drive file requires the viewer's own Drive connection.  A linked Context file
    // is different: the Edge Function uses source_profile_id from the attachment.
    if (!attachmentId && !this.connection.capabilities?.drivePreview) {
      this.previewError = "내 Google Drive read-only 권한을 연결해야 개인 Drive 파일을 Preview할 수 있음.";
      if (rerender) this.render();
      return;
    }

    this.previewLoading = true;
    this.render();
    try {
      if (attachmentId && linkedAttachment?.snapshotStatus === "ready" && linkedAttachment?.storagePath) {
        const cacheKey=`snapshot:${attachmentId}:${linkedAttachment.snapshotAt||""}`;
        const archived=this.previewCache.get(cacheKey)||await this.service.previewFile(this.preview.externalId, this.preview.mimeType, attachmentId);
        this.previewCache.set(cacheKey,archived);
        if (archived?.blob) {
          const archivedMime = archived.contentType || linkedAttachment.snapshotMimeType || this.preview.mimeType || "application/octet-stream";
          this.preview.mimeType = archivedMime;
          if (isOfficeSelfPreviewMime(archivedMime)) {
            this.previewOfficeMime = archivedMime;
            this.previewOfficeBuffer = await archived.blob.arrayBuffer();
          } else {
            this.previewAssetType = archivedMime;
            this.previewAssetUrl = URL.createObjectURL(archived.blob);
          }
          this.previewDetails = { archive: true, snapshotAt: linkedAttachment.snapshotAt, source: "NAYSOR Project Context Snapshot" };
          this.previewError = "";
          return;
        }
      }
      const details = await this.service.fileDetails(this.preview.externalId, this.preview.mimeType, attachmentId).catch(() => null);
      this.previewDetails = details;
      if (details?.resolvedMimeType) this.preview.mimeType = details.resolvedMimeType;
      if (details?.resolvedFileId) this.preview.resolvedFileId = details.resolvedFileId;
      if (details?.file?.webViewLink) this.preview.webViewLink = details.file.webViewLink;
      this.preview.kind = googleFileKind(this.preview.mimeType || "", this.preview.provider || "google_drive");

      if (GOOGLE_NATIVE_MIME.has(this.preview.mimeType)) {
        try {
          this.previewNative = await this.service.nativePreview(this.preview.externalId, this.preview.mimeType, attachmentId);
          if (this.previewNative?.kind === "doc") {
            try {
              const exported = await this.service.previewFile(this.preview.externalId, this.preview.mimeType, attachmentId);
              if (exported?.blob) {
                this.previewAssetType = exported.contentType || "application/pdf";
                this.previewAssetUrl = URL.createObjectURL(exported.blob);
                this.previewMode = "layout";
                this.previewError = "";
                return;
              }
            } catch (exportError) {
              console.warn("Google Docs PDF export failed; using Docs API content preview", exportError);
            }
            this.previewMode = "content";
            this.previewError = "";
            return;
          }
          if (this.previewNative?.kind === "slide") {
            this.previewSlideIndex = 0;
            await this.loadAllSlideThumbnails(false);
            this.previewMode = "content";
            this.previewError = "";
            return;
          }
          if (this.previewNative?.kind === "sheet") {
            this.previewMode = "content";
            this.previewError = "";
            return;
          }
        } catch (nativeError) {
          console.warn("Native Google preview failed; trying NAYSOR relay/binary fallback", nativeError);
        }
      }

      const binary = await this.service.previewFile(this.preview.externalId, this.preview.mimeType, attachmentId);
      if (binary?.blob) {
        const actualMime = binary.resolvedMimeType || binary.contentType || this.preview.mimeType || "application/octet-stream";
        this.preview.mimeType = actualMime;
        if (isOfficeSelfPreviewMime(actualMime)) {
          this.previewOfficeMime = actualMime;
          this.previewOfficeBuffer = await binary.blob.arrayBuffer();
          this.previewMode = "layout";
          this.previewError = "";
          return;
        }
        if (actualMime === "application/pdf" || actualMime.startsWith("image/")) {
          this.previewAssetType = actualMime;
          this.previewAssetUrl = URL.createObjectURL(binary.blob);
          this.previewError = "";
          return;
        }
        this.previewError = `NAYSOR 자체 Viewer가 아직 지원하지 않는 파일 형식임 · ${actualMime}`;
        return;
      }
      this.previewError = `미리보기 데이터가 비어 있음${details?.resolvedMimeType ? ` · ${details.resolvedMimeType}` : ""}`;
    } catch (error) {
      this.previewError = error.message || String(error);
    } finally {
      this.previewLoading = false;
      this.render();
    }
  }

  async loadAllSlideThumbnails(rerender = true) {
    const data = this.previewNative;
    const slides = data?.slides || [];
    if (!slides.length || !this.service) return;
    this.previewSlideBatchLoading = true;
    this.previewSlideUrls = Array(slides.length).fill("");
    if (rerender) this.render();
    try {
      const objectIds = slides.map(slide => slide.objectId).filter(Boolean);
      const linkedAttachment = (this.state.attachments || []).find(row => String(row.id) === String(this.preview?.id));
      const result = await this.service.slideThumbnails(this.preview?.externalId || this.preview?.resolvedFileId, objectIds, linkedAttachment?.id || "");
      const byId = new Map((result?.thumbnails || []).map(item => [item.objectId, item.contentUrl || ""]));
      this.previewSlideUrls = slides.map(slide => byId.get(slide.objectId) || "");
      if (!this.previewSlideUrls.some(Boolean)) this.previewError = "슬라이드 썸네일을 불러오지 못함";
    } catch (error) {
      this.previewError = error.message || String(error);
    } finally {
      this.previewSlideBatchLoading = false;
      if (rerender) this.render();
    }
  }

  async loadSlideThumbnail(index, rerender = true) {
    // v2.3.1 compatibility: legacy prev/next actions now scroll to the requested card.
    const slides = this.previewNative?.slides || [];
    if (!slides.length) return;
    this.previewSlideIndex = Math.max(0, Math.min(Number(index) || 0, slides.length - 1));
    if (!this.previewSlideUrls.length) await this.loadAllSlideThumbnails(false);
    if (rerender) {
      this.render();
      requestAnimationFrame(() => this.root?.querySelector(`[data-slide-index="${this.previewSlideIndex}"]`)?.scrollIntoView?.({ block: "center", behavior: "smooth" }));
    }
  }

  releasePreviewAsset() {
    if (this.previewAssetUrl) URL.revokeObjectURL(this.previewAssetUrl);
    this.previewAssetUrl = "";
    this.previewAssetType = "";
    this.previewEmbedUrl = "";
    this.previewRelay = null;
    this.previewOfficeBuffer = null;
    this.previewOfficeMime = "";
    this.previewOfficeRenderToken += 1;
    for (const url of this.previewOfficeObjectUrls.splice(0)) URL.revokeObjectURL(url);
    this.previewError = "";
    this.previewLoading = false;
    this.previewDetails = null;
    this.previewNative = null;
    this.previewSlideIndex = 0;
    this.previewSlideUrl = "";
    this.previewSlideLoading = false;
    this.previewSlideUrls = [];
    this.previewSlideBatchLoading = false;
    this.previewExpanded = false;
    this.previewZoom = 1;
    this.previewMode = "layout";
  }

  // v2.9: Calendar-to-Gantt synchronization intentionally removed.
  // Calendar events are attached as immutable context snapshots only.


  setLoading(value) {
    this.loading = value;
    this.root?.querySelector(".workspace-drawer")?.classList.toggle("is-loading", value);
  }

  toast(message) {
    window.dispatchEvent(new CustomEvent("naysor:toast", { detail: String(message || "") }));
  }

  decorateEntities() {
    const observer = this.observer;
    observer?.disconnect();
    document.querySelectorAll("[data-google-generated='true']").forEach(node => node.remove());
    const resourceMap = new Map();
    const addResource = (type, id, kind) => {
      if (!type || !id) return;
      const key = `${type}:${id}`;
      const value = resourceMap.get(key) || { total: 0, kinds: new Set() };
      value.total += 1;
      value.kinds.add(kind);
      resourceMap.set(key, value);
    };
    for (const item of this.state.attachments || []) {
      const kind = attachmentKind(item);
      addResource(item.entityType, item.entityId, kind);
      if (kind === "calendar" && meetUrlOf(item.metadata || {})) addResource(item.entityType, item.entityId, "meet");
    }
    const actionMap = {
      "edit-project": "project", "edit-phase": "phase", "select-phase": "phase", "edit-activity": "activity",
      "edit-gate": "gate", "select-gate": "gate", "edit-deliverable": "deliverable", "edit-agreement": "agreement", "edit-risk": "risk",
      "select-organization": "organization", "edit-organization": "organization"
    };
    document.querySelectorAll("[data-action][data-id]").forEach(target => {
      const type = actionMap[target.dataset.action];
      if (!type || !target.dataset.id) return;
      const id = target.dataset.id;
      const resources = resourceMap.get(`${type}:${id}`) || { total: 0, kinds: new Set() };
      const preferred = ["doc", "sheet", "slide", "pdf", "mail", "calendar", "meet", "drive", "file"];
      const kinds = preferred.filter(kind => resources.kinds.has(kind)).slice(0, 4);
      if (!kinds.length && !resources.total) kinds.push("drive");
      const indicator = document.createElement("span");
      indicator.className = `entity-resource-cluster${resources.total ? " has-links" : ""}`;
      indicator.dataset.googleGenerated = "true";
      indicator.dataset.googleEntityType = type;
      indicator.dataset.googleEntityId = id;
      indicator.dataset.googleOpenTab = "context";
      indicator.setAttribute("role", "button");
      indicator.setAttribute("tabindex", "0");
      indicator.setAttribute("title", resources.total ? `연결 Context ${resources.total}개 · 클릭하여 Preview` : "Google Workspace 자료 연결");
      indicator.innerHTML = `<span class="entity-resource-icons">${kinds.map(kind => `<i class="resource-kind-${kind}">${icon(kind)}</i>`).join("")}</span>${resources.total ? `<b>${resources.total}</b>` : ""}`;
      if (target.classList.contains("gantt-line")) target.querySelector(".task-name")?.appendChild(indicator);
      else if (target.tagName === "TR") target.firstElementChild?.appendChild(indicator);
      else target.appendChild(indicator);
    });
    document.querySelectorAll("form[data-form]").forEach(form => {
      const type = form.dataset.form;
      if (!SUPPORTED_ENTITIES.has(type)) return;
      const id = form.elements.originalId?.value || form.elements.id?.value || "new";
      if (id === "new" || !id) return;
      const actions = form.querySelector(".form-actions");
      if (!actions) return;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "button google-form-link-button";
      button.dataset.googleGenerated = "true";
      button.dataset.googleEntityType = type;
      button.dataset.googleEntityId = id;
      button.dataset.googleOpenTab = "context";
      button.innerHTML = `${icon("link")} Context 자료`;
      actions.insertBefore(button, actions.lastElementChild || null);
    });
    observer?.observe(document.body, { childList: true, subtree: true });
  }
}

export async function startGoogleWorkspaceUI(platform) {
  const ui = new GoogleWorkspaceUI(platform);
  await ui.start();
  return ui;
}
